import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Card, Heading, Text, Select, Textarea } from "@chakra-ui/react";
import Axios from "../utils/httpClient";
import { kpiSections } from "./kpiData";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FILE_URL, API_HOST } from "../utils/api";
import { useSelector } from "react-redux";
import Layout from "./Layout";

const PRIMARY = '#133654';

const fileHref = (fpath) => {
  if (!fpath) return '#';
  if (String(fpath).startsWith('http')) return fpath;
  if (String(fpath).startsWith('/')) return `http://${API_HOST}${fpath}`;
  return `${FILE_URL}${fpath}`;
};

const statusBg = (s, app, userPartitionName) => {
  const code = app?.parameter?.code || app?.parameter?.code;

  // Determine current department
  const departmentName = (userPartitionName || '').toLowerCase();
  const isTT = departmentName.includes('tarmoq texnologiyalari');
  const isTS = departmentName.includes("ta'lim sifati") || departmentName.includes("talim sifati");
  const isOV = departmentName.includes("o'quv") || departmentName.includes("oquv");
  const isMN = departmentName.includes("ma'naviyat") || departmentName.includes("manaviyat");
  const isXL = departmentName.includes("xalqaro hamkorlik");
  const isIL = departmentName.includes("ilmiy bo'lim");
  const isMK = departmentName.includes("marketing");

  // Multi-department approval logic - only show THIS department's status
  if (code === '1.1' && (app?.tt_status !== undefined || app?.ts_status !== undefined || app?.ov_status !== undefined)) {
    let myStatus = 0; // 0 = not decided, 1 = approved, 2 = rejected

    if (isTT) myStatus = app.tt_status || 0;
    else if (isTS) myStatus = app.ts_status || 0;
    else if (isOV) myStatus = app.ov_status || 0;

    if (myStatus === 2) return 'rgba(231, 76, 60, 0.15)'; // This dept rejected
    if (myStatus === 1) return 'rgba(46, 204, 113, 0.15)'; // This dept approved
    return 'rgba(241, 196, 15, 0.25)'; // This dept hasn't decided
  }

  if (code === '1.3' || code === '1.4') {
    let myStatus = 0;

    if (isMN) myStatus = app.mn_status || 0;
    else if (isOV) myStatus = app.ov_status || 0;

    if (myStatus === 2) return 'rgba(231, 76, 60, 0.15)';
    if (myStatus === 1) return 'rgba(46, 204, 113, 0.15)';
    return 'rgba(241, 196, 15, 0.25)';
  }

  if (code === '3.2' || code === '3.3') {
    let myStatus = 0;

    if (isOV) myStatus = app.ov_status || 0;
    else if (isXL) myStatus = app.tt_status || 0;
    else if (isIL) myStatus = app.ts_status || 0;

    if (myStatus === 2) return 'rgba(231, 76, 60, 0.15)';
    if (myStatus === 1) return 'rgba(46, 204, 113, 0.15)';
    return 'rgba(241, 196, 15, 0.25)';
  }

  if (code === '3.1' || code === '2.1' || code === '3.4') {
    let myStatus = 0;

    if (isXL) myStatus = app.tt_status || 0;
    else if (isIL) myStatus = app.ts_status || 0;

    if (myStatus === 2) return 'rgba(231, 76, 60, 0.15)';
    if (myStatus === 1) return 'rgba(46, 204, 113, 0.15)';
    return 'rgba(241, 196, 15, 0.25)';
  }

  // KPI 2.9: Dual-approval by Marketing (mn_status) and Ilmiy (ts_status)


  // Explicit handling for single-department KPIs
  // 1.2, 1.5: Ta'lim sifati bo'limi
  // 1.6, 1.7, 1.8, 1.9: O'quv bo'limi  
  // 2.x: Ilmiy tadqiqotlar bo'limi
  // 4.x: Ma'naviyat bo'limi
  if (code === '1.2' || code === '1.5' ||
    code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9' ||
    code.startsWith('2.') || code.startsWith('4.')) {
    if (s === 5) return 'rgba(231, 76, 60, 0.15)';
    if (s === 2 || s === 3 || s === 4) return 'rgba(46, 204, 113, 0.15)';
    return 'rgba(241, 196, 15, 0.25)';
  }

  // Default status logic
  if (s === 5) return 'rgba(231, 76, 60, 0.15)';
  if (s === 2 || s === 3 || s === 4) return 'rgba(46, 204, 113, 0.15)';
  return 'rgba(241, 196, 15, 0.25)';
};

const statusText = (s, app, userPartitionName) => {
  const code = app?.parameter?.code || app?.parameter?.code;

  // Determine current department
  const departmentName = (userPartitionName || '').toLowerCase();
  const isTT = departmentName.includes('tarmoq texnologiyalari');
  const isTS = departmentName.includes("ta'lim sifati") || departmentName.includes("talim sifati");
  const isOV = departmentName.includes("o'quv") || departmentName.includes("oquv");
  const isMN = departmentName.includes("ma'naviyat") || departmentName.includes("manaviyat");
  const isXL = departmentName.includes("xalqaro hamkorlik");
  const isIL = departmentName.includes("ilmiy bo'lim");
  const isMK = departmentName.includes("marketing");

  // Multi-department approval logic - only show THIS department's status
  if (code === '1.1' && (app?.tt_status !== undefined || app?.ts_status !== undefined || app?.ov_status !== undefined)) {
    let myStatus = 0;

    if (isTT) myStatus = app.tt_status || 0;
    else if (isTS) myStatus = app.ts_status || 0;
    else if (isOV) myStatus = app.ov_status || 0;

    if (myStatus === 2) return 'Rad etildi';
    if (myStatus === 1) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  if (code === '1.3' || code === '1.4') {
    let myStatus = 0;

    if (isMN) myStatus = app.mn_status || 0;
    else if (isOV) myStatus = app.ov_status || 0;

    if (myStatus === 2) return 'Rad etildi';
    if (myStatus === 1) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  if (code === '3.2' || code === '3.3') {
    let myStatus = 0;

    if (isOV) myStatus = app.ov_status || 0;
    else if (isXL) myStatus = app.tt_status || 0;
    else if (isIL) myStatus = app.ts_status || 0;

    if (myStatus === 2) return 'Rad etildi';
    if (myStatus === 1) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  if (code === '3.1' || code === '2.1' || code === '3.4') {
    let myStatus = 0;

    if (isXL) myStatus = app.tt_status || 0;
    else if (isIL) myStatus = app.ts_status || 0;

    if (myStatus === 2) return 'Rad etildi';
    if (myStatus === 1) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  // KPI 2.9: Dual-approval by Marketing (mn_status) and Ilmiy (ts_status)


  // Explicit handling for single-department KPIs using department-specific status fields
  // 1.2, 1.5: Ta'lim sifati bo'limi (uses ts_status)
  if ((code === '1.2' || code === '1.5') && isTS) {
    let myStatus = app.ts_status || 0;
    if (myStatus === 2) return 'Rad etildi';
    if (myStatus === 1) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  // 1.6, 1.7, 1.8, 1.9: O'quv bo'limi (uses ov_status)
  if ((code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9') && isOV) {
    let myStatus = app.ov_status || 0;
    if (myStatus === 2) return 'Rad etildi';
    if (myStatus === 1) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  // 2.x: Ilmiy tadqiqotlar bo'limi (uses ts_status)
  if (code.startsWith('2.') && isIL) {
    let myStatus = app.ts_status || 0;
    if (myStatus === 2) return 'Rad etildi';
    if (myStatus === 1) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  // 4.x: Ma'naviyat bo'limi (uses mn_status)
  if (code.startsWith('4.') && isMN) {
    let myStatus = app.mn_status || 0;
    if (myStatus === 2) return 'Rad etildi';
    if (myStatus === 1) return 'Tasdiqlandi';
    return 'Jarayonda';
  }

  // Fallback for single-department KPIs not handled above
  if (code === '1.2' || code === '1.5' ||
    code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9' ||
    code.startsWith('2.') || code.startsWith('4.')) {
    if (s === 5) return "Rad etildi";
    if (s === 2 || s === 3 || s === 4) return "Tasdiqlandi";
    return "Jarayonda";
  }

  // Default status logic
  if (s === 5) return "Rad etildi";
  if (s === 2 || s === 3 || s === 4) return "Tasdiqlandi";
  return "Jarayonda";
};

const KpiDetailDepartment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector(s => s);

  // Initialize activeTab from URL parameter to avoid race condition
  const getInitialTab = () => {
    const filter = searchParams.get('filter');
    if (filter === 'baholangan' || filter === 'baholanganlar') {
      return 'baholangan';
    }
    return 'baholanmagan';
  };

  const [myApps, setMyApps] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedPoints, setSelectedPoints] = useState({}); // New state for tracking selected points
  const [activeTab, setActiveTab] = useState(getInitialTab()); // Initialize from URL
  const [rejectionComments, setRejectionComments] = useState({}); // State for rejection comments per application
  const [showDetailsForApp, setShowDetailsForApp] = useState({}); // State for tracking which apps show detailed comments

  const [paramFromApi, setParamFromApi] = useState(null);

  const param = useMemo(() => {
    const all = kpiSections.flatMap(s => s.items);
    const foundParam = all.find(p => String(p.no) === String(id));
    // Merge with API data if available, but prioritize static data over API data
    // This ensures that static data (which works correctly) takes precedence
    const merged = paramFromApi ? { ...paramFromApi, ...foundParam } : foundParam;
    console.log('📦 Param merged (static data prioritized):', {
      hasApiData: !!paramFromApi,
      hasStaticData: !!foundParam,
      apiRequiredPercentage: paramFromApi?.required_percentage,
      staticMaxBall: foundParam?.maxBall,
      mergedRequiredPercentage: merged?.required_percentage,
      mergedMaxBall: merged?.maxBall
    });
    return merged;
  }, [id, paramFromApi]);

  // Fetch parameter details from API to get required_percentage
  useEffect(() => {
    if (id) {
      console.log('🔄 Fetching parameter details for code:', id);
      // Fetch all parameters and find by code
      Axios().get('api/v1/application/kpi-parameters/')
        .then(res => {
          const categories = res.data?.categories || [];
          const allParams = categories.flatMap(cat => cat.parameters || []);
          const foundParam = allParams.find(p => String(p.code) === String(id));
          if (foundParam) {
            console.log('✅ Parameter API response:', foundParam);
            console.log('✅ Setting paramFromApi with required_percentage:', foundParam.required_percentage);
            setParamFromApi(foundParam);
          } else {
            console.log('⚠️ Parameter not found in API response for code:', id);
          }
        })
        .catch(err => {
          console.error('❌ Error loading parameter details:', err);
        });
    }
  }, [id]);

  const loadDepartmentApps = () => {
    if (!param?.no) return;
    setLoadingList(true);

    // Choose endpoint based on active tab
    const endpoint = activeTab === 'baholangan' ? 'api/v1/application/list/?all_time=true' : 'api/v1/application/check-list/?all_time=true';

    console.log(`Loading applications for tab: ${activeTab}, endpoint: ${endpoint}`);

    // Load applications for department review (not user's own applications)
    Axios().get(endpoint)
      .then(res => {
        const applications = res?.data?.applications || res?.data || [];
        console.log(`Department loaded applications (${activeTab}):`, applications.length);
        console.log(`Sample applications:`, applications.slice(0, 3).map(app => ({
          id: app.id,
          parameter_code: app.parameter?.code,
          status: app.status,
          tt_status: app.tt_status,
          ts_status: app.ts_status,
          ov_status: app.ov_status,
          mn_status: app.mn_status
        })));

        // Special debugging for parameter 1.6
        if (String(param.no) === '1.6') {
          const param16Apps = applications.filter(app => String(app?.parameter?.code) === '1.6');
          console.log(`🔍 Parameter 1.6 applications found:`, param16Apps.length);
          param16Apps.forEach((app, index) => {
            console.log(`Parameter 1.6 App ${index + 1}:`, {
              id: app.id,
              status: app.status,
              ov_status: app.ov_status,
              ball: app.ball,
              user: app.user?.first_name || app.user?.username,
              partition_id: app.partition_id,
              created_at: app.created_at
            });
          });
        }

        // Filter applications based on parameter and tab
        const filteredApps = applications.filter(app => {
          const appCode = String(app?.parameter?.code);
          const paramCode = String(param.no);
          const isMatch = appCode === paramCode;

          if (!isMatch) return false;

          // Additional filtering based on active tab
          if (activeTab === 'baholangan') {
            // For evaluated applications, show all applications that have been evaluated
            // This is a simplified approach to debug the issue
            console.log(`Checking evaluated app ${app.id}:`, {
              appCode,
              app_status: app.status,
              ball: app.ball,
              tt_status: app.tt_status,
              ts_status: app.ts_status,
              ov_status: app.ov_status,
              mn_status: app.mn_status,
              userPartition: user?.partition?.name
            });

            // Check if THIS specific department has evaluated this application
            const departmentName = user?.partition?.name?.toLowerCase() || "";
            const isTT = departmentName.includes("tarmoq texnologiyalari");
            const isTS = departmentName.includes("ta'lim sifati") || departmentName.includes("talim sifati");
            // More comprehensive O'quv bo'limi detection - handle all possible variations
            const isOV = departmentName.includes("o'quv") ||
              departmentName.includes("oquv") ||
              departmentName.includes("oâ€˜quv") ||
              departmentName.includes("o'quv bo'limi") ||
              departmentName.includes("oquv bo'limi") ||
              departmentName === "o'quv bo'limi" ||
              departmentName.includes("o'quv-uslubiy") ||
              departmentName.includes("oquv-uslubiy") ||
              user?.partition?.name?.toLowerCase().includes("o'quv") ||
              user?.partition?.name?.toLowerCase().includes("oquv");
            const isMN = departmentName.includes("ma'naviyat") || departmentName.includes("manaviyat") || departmentName.includes("maâ€™naviyat") || departmentName.includes("ma`naviyat") || departmentName.includes("ma'naviyat bo'limi");
            const isXL = departmentName.includes("xalqaro hamkorlik");
            const isIL = departmentName.includes("ilmiy bo'lim");
            const isMK = departmentName.includes("marketing");

            // Debug department detection for parameter 1.6
            if (String(app?.parameter?.code) === '1.6') {
              console.log(`🏢 Parameter 1.6 DEPARTMENT DETECTION:`, {
                originalName: user?.partition?.name,
                lowerCaseName: departmentName,
                isTT, isTS, isOV, isMN, isXL, isIL,
                containsOquv: departmentName.includes("o'quv"),
                containsOquv2: departmentName.includes("oquv")
              });
            }

            let evaluatedByThisDept = false;

            // Check department-specific evaluation status
            if (appCode === '1.1') {
              if (isTT) evaluatedByThisDept = app.tt_status === 1 || app.tt_status === 2;
              else if (isTS) evaluatedByThisDept = app.ts_status === 1 || app.ts_status === 2;
              else if (isOV) evaluatedByThisDept = app.ov_status === 1 || app.ov_status === 2;
            } else if (appCode === '1.3' || appCode === '1.4') {
              if (isMN) evaluatedByThisDept = app.mn_status === 1 || app.mn_status === 2;
              else if (isOV) evaluatedByThisDept = app.ov_status === 1 || app.ov_status === 2;
            } else if (appCode === '3.2' || appCode === '3.3') {
              if (isOV) evaluatedByThisDept = app.ov_status === 1 || app.ov_status === 2;
              else if (isXL) evaluatedByThisDept = app.tt_status === 1 || app.tt_status === 2;
              else if (isIL) evaluatedByThisDept = app.ts_status === 1 || app.ts_status === 2;
            } else if (appCode === '3.4') {
              if (isXL) evaluatedByThisDept = app.tt_status === 1 || app.tt_status === 2;
              else if (isIL) evaluatedByThisDept = app.ts_status === 1 || app.ts_status === 2;
            } else if (appCode === '3.1' || appCode === '2.1') {
              if (isXL) evaluatedByThisDept = app.tt_status === 1 || app.tt_status === 2;
              else if (isIL) evaluatedByThisDept = app.ts_status === 1 || app.ts_status === 2;

            } else {
              // For single-department KPIs, check department-specific status fields
              if ((appCode === '1.2' || appCode === '1.5') && isTS) {
                evaluatedByThisDept = app.ts_status === 1 || app.ts_status === 2;
              } else if (appCode === '1.6' || appCode === '1.7' || appCode === '1.8' || appCode === '1.9') {
                // For 1.6-1.9, check if this user's partition is responsible for these parameters
                // These parameters are assigned to O'quv bo'limi, so check ov_status
                evaluatedByThisDept = app.ov_status === 1 || app.ov_status === 2;
              } else if (appCode.startsWith('2.') && isIL) {
                evaluatedByThisDept = app.ts_status === 1 || app.ts_status === 2; // IL uses ts_status field
              } else if (appCode.startsWith('4.') && isMN) {
                evaluatedByThisDept = app.mn_status === 1 || app.mn_status === 2;
              } else {
                // Fallback: check global status
                // Status 4 = Approved, Status 5 = Rejected
                // Status 2 = Seen but not evaluated (should stay in baholanmagan)
                evaluatedByThisDept = app.status === 4 || app.status === 5;
              }
            }

            console.log(`App ${app.id} department evaluation check:`, {
              departmentName,
              isTT, isTS, isOV, isMN, isXL, isIL,
              evaluatedByThisDept,
              appCode,
              app_status: app.status,
              ov_status: app.ov_status,
              ts_status: app.ts_status,
              mn_status: app.mn_status,
              will_show: evaluatedByThisDept
            });

            // Special debugging for parameter 1.6
            if (appCode === '1.6') {
              console.log(`🚨 Parameter 1.6 EVALUATION CHECK:`, {
                appId: app.id,
                isOV,
                ov_status: app.ov_status,
                evaluatedByThisDept,
                departmentName,
                activeTab
              });
            }

            // Special debugging for parameter 1.6
            if (appCode === '1.6') {
              console.log(`🔥 Parameter 1.6 FINAL RESULT:`, {
                appId: app.id,
                evaluatedByThisDept,
                willBeIncluded: evaluatedByThisDept
              });
            }

            return evaluatedByThisDept;
          } else {
            // For pending applications (baholanmagan), keep existing logic
            return true;
          }
        });

        console.log(`Filtered apps for ${activeTab}:`, filteredApps.length);

        // Debug the final result for parameter 1.6
        if (String(param.no) === '1.6') {
          console.log(`🎯 Parameter 1.6 FINAL RESULTS for ${activeTab}:`, {
            filteredAppsCount: filteredApps.length,
            filteredApps: filteredApps.map(app => ({
              id: app.id,
              status: app.status,
              ov_status: app.ov_status,
              user: app.user?.first_name
            }))
          });
        }
        if (String(param.no) === '1.1') {
          filteredApps.forEach((app, index) => {
            console.log(`KPI 1.1 App ${index + 1} (${activeTab}):`, {
              id: app.id,
              user: app.user?.first_name,
              status: app.status,
              statusText: app.status === 1 ? 'Teacher Application' : app.status === 6 ? 'Requirement' : 'Other',
              hasVideoField: app.work?.includes('videoLessonsCount') || app.work?.includes('videoLessons'),
              hasTotalField: app.work?.includes('totalLessons'),
              work: app.work?.substring(0, 100) + '...'
            });
          });
        }

        setMyApps(filteredApps);

        // Debug for parameter 1.6 - track when setMyApps is called
        if (String(param.no) === '1.6') {
          console.log(`🎯 Parameter 1.6 setMyApps called:`, {
            activeTab,
            filteredAppsCount: filteredApps.length,
            timestamp: new Date().toISOString()
          });
        }

        // Only mark applications as seen for pending applications (baholanmagan tab)
        if (activeTab === 'baholanmagan') {
          const unseenApps = filteredApps.filter(app => {
            const appCode = String(app?.parameter?.code);
            const departmentName = user?.partition?.name?.toLowerCase() || "";
            const isTT = departmentName.includes("tarmoq texnologiyalari");
            const isTS = departmentName.includes("ta'lim sifati") || departmentName.includes("talim sifati");
            const isOV = departmentName.includes("o'quv") || departmentName.includes("oquv");
            const isMN = departmentName.includes("ma'naviyat") || departmentName.includes("manaviyat");
            const isXL = departmentName.includes("xalqaro hamkorlik");
            const isIL = departmentName.includes("ilmiy bo'lim");
            const isMK = departmentName.includes("marketing");

            // Check if application is unseen by this specific department
            if (appCode === '1.1') {
              if (isTT) return app.tt_status === 0;
              else if (isTS) return app.ts_status === 0;
              else if (isOV) return app.ov_status === 0;
            } else if (appCode === '1.3' || appCode === '1.4') {
              if (isMN) return app.mn_status === 0;
              else if (isOV) return app.ov_status === 0;
            } else if (appCode === '3.2' || appCode === '3.3') {
              if (isOV) return app.ov_status === 0;
              else if (isXL) return app.tt_status === 0;
              else if (isIL) return app.ts_status === 0;
            } else if (appCode === '3.4') {
              if (isXL) return app.tt_status === 0;
              else if (isIL) return app.ts_status === 0;
            } else if (appCode === '3.1' || appCode === '2.1') {
              if (isXL) return app.tt_status === 0;
              else if (isIL) return app.ts_status === 0;

            } else if ((appCode === '1.2' || appCode === '1.5') && isTS) {
              return app.ts_status === 0;
            } else if ((appCode === '1.6' || appCode === '1.7' || appCode === '1.8' || appCode === '1.9') && isOV) {
              return app.ov_status === 0;
            } else if (appCode.startsWith('2.') && isIL) {
              return app.ts_status === 0; // IL uses ts_status field
            } else if (appCode.startsWith('4.') && isMN) {
              return app.mn_status === 0;
            } else {
              // Fallback: use global status
              return app.status === 1;
            }
            return false;
          });
          if (unseenApps.length > 0) {
            unseenApps.forEach(app => {
              // Mark each application as seen
              Axios().post(`api/v1/application/mark-as-seen/${app.id}/`)
                .then(() => {
                  console.log(`Marked application ${app.id} as seen`);
                })
                .catch(err => {
                  console.error(`Error marking application ${app.id} as seen:`, err);
                });
            });
          }
        }
      })
      .catch(e => {
        console.error('Error loading applications:', e);
        setMyApps([]);
      })
      .finally(() => setLoadingList(false));

    // Reload requirement apps for KPI 1.1 if needed
    if (String(param?.no) === '1.1') {
      loadKpi11RequirementApps();
    }
  };

  // Calculate points for KPI 1.1 based on video lessons percentage
  const calculateKPI11Points = (app, maxBallString, requirementApps = []) => {
    try {
      console.log('Calculating KPI 1.1 points for app:', app?.id, 'status:', app?.status, 'with requirements:', requirementApps?.length);

      // Make sure we're reading video lessons from a TEACHER'S APPLICATION (status = 1, not 6)
      // Status 1 = Created/Submitted by teacher
      // Status 6 = Requirement sent by department
      if (app.status === 6) {
        console.log('This is a requirement app (status=6), not a teacher application');
        return 0;
      }

      if (app.status !== 1) {
        console.log('This app has status', app.status, 'expected status=1 for teacher application');
        // Continue anyway, might still work
      }

      const teacherData = JSON.parse(app.work || '{}');
      console.log('Raw teacher app.work:', app.work);
      console.log('Parsed teacher data:', teacherData);
      console.log('Available fields in teacher data:', Object.keys(teacherData));

      // Log the actual raw string to see what's really there
      console.log('ACTUAL RAW WORK DATA:', app.work);

      // Try to extract video lessons count with more comprehensive field checking
      let videoLessons = 0;

      // Check all possible field names for video lessons
      const videoFields = ['videoLessonsCount', 'videoLessons', 'videoDarslarSoni', 'video_darslar_soni'];
      for (const field of videoFields) {
        if (teacherData[field] !== undefined && teacherData[field] !== null && teacherData[field] !== '') {
          const value = parseInt(teacherData[field]);
          if (!isNaN(value) && value > 0) {
            videoLessons = value;
            console.log(`Found video lessons in field '${field}':`, videoLessons);
            break;
          } else {
            console.log(`Field '${field}' has value:`, teacherData[field], 'parsed as:', value);
          }
        } else {
          console.log(`Field '${field}' not found or is null/undefined/empty`);
        }
      }

      // If still not found, try to parse the raw work string
      if (videoLessons === 0) {
        const workString = app.work || '';
        console.log('Parsing raw work string for video lessons:', workString);

        // Try various patterns to find video lessons count
        const patterns = [
          /videoLessonsCount["\s:]*([0-9]+)/i,
          /videoLessons["\s:]*([0-9]+)/i,
          /videoDarslarSoni["\s:]*([0-9]+)/i,
          /"videoLessonsCount"["\s:]*([0-9]+)/i,
          /"videoLessons"["\s:]*([0-9]+)/i,
          /"videoDarslarSoni"["\s:]*([0-9]+)/i
        ];

        for (const pattern of patterns) {
          const match = workString.match(pattern);
          if (match) {
            videoLessons = parseInt(match[1]) || 0;
            console.log('Found video lessons with pattern:', pattern, 'value:', videoLessons);
            break;
          }
        }
      }

      console.log('Final video lessons count:', videoLessons);

      // Additional debugging - check if the value is actually zero or just falsy
      if (videoLessons === 0) {
        console.log('Video lessons is zero, checking individual field values:');
        Object.keys(teacherData).forEach(key => {
          const value = teacherData[key];
          console.log(`  ${key}:`, value, 'type:', typeof value);
          if (typeof value === 'string' && value.match(/^\d+$/)) {
            const numValue = parseInt(value);
            if (numValue > 0) {
              console.log(`    ^ Found potential video count: ${key} = ${numValue}`);
              videoLessons = numValue;
            }
          }
        });
      }

      // Debug: Show ALL fields and their values to find the correct field name
      console.log('ALL TEACHER DATA FIELDS:');
      Object.keys(teacherData).forEach(key => {
        const value = teacherData[key];
        console.log(`  ${key}: ${value} (type: ${typeof value})`);

        // Check if this field might contain video lessons count
        if (typeof value === 'string' || typeof value === 'number') {
          const numValue = parseInt(value);
          if (numValue > 0 && numValue < 100) {
            console.log(`    ^ Potential video lessons field: ${key} = ${numValue}`);
          }
        }
      });

      // Try alternative field names for video lessons
      let finalVideoLessons = videoLessons;
      if (videoLessons === 0) {
        // Since we found videoDarslarSoni = 12, let's extract it directly
        if (teacherData.videoDarslarSoni) {
          finalVideoLessons = parseInt(teacherData.videoDarslarSoni) || 0;
          console.log('Found video lessons in videoDarslarSoni:', finalVideoLessons);
        } else {
          // Try other possible field names
          const alternativeFields = [
            'video_darslar_soni', 'videoDarslar',
            'videoCount', 'darslarSoni', 'lessons', 'videoLessons'
          ];

          for (const field of alternativeFields) {
            if (teacherData[field]) {
              finalVideoLessons = parseInt(teacherData[field]) || 0;
              console.log(`Found video lessons in field '${field}':`, finalVideoLessons);
              break;
            }
          }
        }

        // If still no luck, try to find any field with value 12
        if (finalVideoLessons === 0) {
          console.log('Searching for field with value 12...');
          Object.keys(teacherData).forEach(key => {
            const value = teacherData[key];
            if (parseInt(value) === 12) {
              finalVideoLessons = 12;
              console.log(`Found video lessons (12) in field '${key}':`, value);
            }
          });
        }

        // If still no video lessons, check if the data is in a different format
        if (finalVideoLessons === 0) {
          console.log('Checking for video lessons in string format...');
          const workString = app.work || '';
          const videoMatch = workString.match(/video.*?(\d+)/i) || workString.match(/(\d+).*?video/i);
          if (videoMatch) {
            finalVideoLessons = parseInt(videoMatch[1]) || 0;
            console.log('Found video lessons in text:', finalVideoLessons);
          }
        }
      }

      console.log('KPI 1.1 Calculation - Video lessons:', videoLessons, 'Requirement apps:', requirementApps.length);
      console.log('All requirement apps:', requirementApps);

      // Extract numeric value from maxBall string (e.g., "5 ball" -> 5)
      const maxBall = parseInt(maxBallString) || 0;

      // Get total lessons count from the teacher's application (they received it from department)
      let totalLessons = 0;

      // First, try to get totalLessonsRequirement from the teacher's application itself
      // ALWAYS look for requirement applications from department FIRST
      // This ensures we use the latest requirement, not old data from teacher's submission
      if (requirementApps.length > 0) {
        console.log('🔍 DEBUGGING REQUIREMENT FILTERING:');
        console.log('  Teacher ID we are looking for:', app.user?.id, '(' + app.user?.first_name + ' ' + app.user?.last_name + ')');
        console.log('  Total requirement apps available:', requirementApps.length);

        // Debug each requirement app
        requirementApps.forEach((reqApp, idx) => {
          console.log(`  Requirement app ${idx + 1}:`, {
            id: reqApp.id,
            parameter_code: reqApp.parameter?.code,
            status: reqApp.status,
            user_id: reqApp.user?.id,
            user_name: reqApp.user?.first_name + ' ' + reqApp.user?.last_name,
            has_totalLessons: reqApp.work?.includes('totalLessons'),
            matches_parameter: reqApp.parameter?.code === '1.1',
            matches_status: reqApp.status === 6,
            matches_user: reqApp.user?.id === app.user?.id,
            user_id_match: `${reqApp.user?.id} === ${app.user?.id} ? ${reqApp.user?.id === app.user?.id}`,
            work_preview: reqApp.work?.substring(0, 100)
          });
        });

        // Find requirements for this teacher: status=6, parameter=1.1, user_id matches
        let kpi11RequirementApps = requirementApps.filter(reqApp =>
          reqApp.parameter?.code === '1.1' &&
          reqApp.status === 6 &&  // Status 6 = Requirement (Not Considered)
          reqApp.user?.id === app.user?.id  // Match specific teacher
          // Removed totalLessons check - we'll extract it from any requirement
        );

        console.log('✅ Found requirement apps for this teacher:', kpi11RequirementApps.length);

        // Debug: Show what we found
        kpi11RequirementApps.forEach((reqApp, index) => {
          console.log(`Requirement candidate ${index + 1}:`, {
            id: reqApp.id,
            status: reqApp.status,
            user: reqApp.user?.first_name,
            hasPlatform: reqApp.work?.includes('platformName'),
            hasTotal: reqApp.work?.includes('totalLessons'),
            has32: reqApp.work?.includes('32'),
            workPreview: reqApp.work?.substring(0, 150)
          });
        });

        console.log('Available requirement apps:', requirementApps.length);
        console.log('Matching KPI 1.1 requirement apps:', kpi11RequirementApps.length);

        console.log('Filtered requirement apps for this teacher:', kpi11RequirementApps);

        if (kpi11RequirementApps.length > 0) {
          // Use the most recent requirement app
          const requirementApp = kpi11RequirementApps[0];
          try {
            // Extract total lessons from the work text which contains JSON data
            const workText = requirementApp.work || '';
            console.log('Raw requirement work text:', workText);
            console.log('Work text length:', workText.length);
            console.log('Contains totalLessons:', workText.includes('totalLessons'));
            console.log('Contains "32":', workText.includes('32'));

            // Don't redeclare - use the outer totalLessons variable
            // let totalLessons = 0;  // REMOVED - was shadowing outer variable

            // Try multiple approaches to extract totalLessons

            // Approach 1: Parse as JSON directly (if it's a single JSON object)
            try {
              const requirementData = JSON.parse(workText);
              console.log('Parsed requirement JSON:', requirementData);
              console.log('Available fields in requirement:', Object.keys(requirementData));
              if (requirementData.totalLessons) {
                totalLessons = parseInt(requirementData.totalLessons) || 0;
                console.log('Found totalLessons in JSON:', totalLessons);
              }
            } catch (e) {
              console.log('Not valid single JSON, trying other approaches...');
            }

            // Approach 2: Look for JSON in separate lines (based on backend format)
            if (totalLessons === 0) {
              const lines = workText.split('\n');
              console.log('Lines in work text:', lines);
              for (const line of lines) {
                const trimmedLine = line.trim();
                console.log('Checking line:', trimmedLine);
                if (trimmedLine.startsWith('{') && trimmedLine.endsWith('}') && trimmedLine.includes('totalLessons')) {
                  try {
                    const lineData = JSON.parse(trimmedLine);
                    // Check both field names
                    if (lineData.totalLessons) {
                      totalLessons = parseInt(lineData.totalLessons) || 0;
                      console.log('Found totalLessons in line JSON:', totalLessons);
                      break;
                    } else if (lineData.totalLessonsRequirement) {
                      totalLessons = parseInt(lineData.totalLessonsRequirement) || 0;
                      console.log('Found totalLessonsRequirement in line JSON:', totalLessons);
                      break;
                    }
                  } catch (e) {
                    console.log('Line is not valid JSON:', e);
                  }
                }
              }
            }

            // Approach 3: Try regex patterns on the entire text
            if (totalLessons === 0) {
              // Try both field names
              let jsonMatch = workText.match(/\{"totalLessons":\s*"?(\d+)"?\}/);
              if (!jsonMatch) {
                jsonMatch = workText.match(/\{"totalLessonsRequirement":\s*"?(\d+)"?\}/);
              }
              if (!jsonMatch) {
                jsonMatch = workText.match(/\{[^}]*"totalLessons":\s*"?(\d+)"?[^}]*\}/);
              }
              if (!jsonMatch) {
                jsonMatch = workText.match(/\{[^}]*"totalLessonsRequirement":\s*"?(\d+)"?[^}]*\}/);
              }
              if (!jsonMatch) {
                // Try to find any JSON with totalLessons or totalLessonsRequirement
                const jsonMatches = workText.match(/\{[^}]*total[Ll]essons[^}]*\}/gi);
                if (jsonMatches) {
                  console.log('Found JSON matches:', jsonMatches);
                  jsonMatch = jsonMatches[0].match(/total[Ll]essons(?:Requirement)?"?:\s*"?(\d+)"?/i);
                }
              }

              console.log('Regex match found:', jsonMatch);
              if (jsonMatch) {
                if (jsonMatch[1]) {
                  // Direct number match from regex group
                  totalLessons = parseInt(jsonMatch[1]) || 0;
                  console.log('Extracted from regex:', totalLessons);
                } else {
                  // Try to parse full JSON
                  try {
                    const requirementData = JSON.parse(jsonMatch[0]);
                    totalLessons = parseInt(requirementData.totalLessons) || 0;
                    console.log('Parsed from matched JSON:', totalLessons);
                  } catch (e) {
                    console.error('JSON parse error:', e);
                  }
                }
              }
            }

            // Approach 4: Fallback to extract totalLessons as a standalone number in the text
            if (totalLessons === 0) {
              const totalLessonsMatch = workText.match(/total[Ll]essons(?:Requirement)?["\s:]*([0-9]+)/i);
              if (totalLessonsMatch) {
                totalLessons = parseInt(totalLessonsMatch[1]) || 0;
                console.log('Fallback extracted totalLessons:', totalLessons);
              }
            }

            // Approach 5: Look for any number that might be total lessons (20-50 range)
            if (totalLessons === 0) {
              const numberMatches = workText.match(/\d+/g);
              if (numberMatches) {
                console.log('Found numbers in requirement:', numberMatches);
                // Look for a reasonable total lessons number (20-50 range)
                for (const num of numberMatches) {
                  const numValue = parseInt(num);
                  if (numValue >= 20 && numValue <= 50) {
                    totalLessons = numValue;
                    console.log('Using number as total lessons:', totalLessons);
                    break;
                  }
                }
              }
            }

            console.log('FINAL EXTRACTED TOTAL LESSONS:', totalLessons);

            // Additional check: Look for teacher-specific total lessons in the requirement data
            if (totalLessons === 0) {
              try {
                const requirementData = JSON.parse(workText);
                console.log('Looking for teacher-specific data in requirement:', requirementData);

                // Check if this requirement contains data for the specific teacher
                if (requirementData.teachers && Array.isArray(requirementData.teachers)) {
                  const teacherData = requirementData.teachers.find(t => t.userId === app.user?.id);
                  if (teacherData && teacherData.totalLessons) {
                    totalLessons = parseInt(teacherData.totalLessons) || 0;
                    console.log('Found teacher-specific total lessons:', totalLessons);
                  }
                }

                // Also check for direct field names that might contain the total lessons
                const possibleFields = ['darslarSoni', 'totalLessons', 'lessons', 'umumiyDarslar'];
                for (const field of possibleFields) {
                  if (requirementData[field] && totalLessons === 0) {
                    totalLessons = parseInt(requirementData[field]) || 0;
                    console.log(`Found total lessons in field '${field}':`, totalLessons);
                    break;
                  }
                }
              } catch (e) {
                console.log('Could not parse requirement data for teacher-specific extraction:', e);
              }
            }
          } catch (e) {
            console.error('Error parsing requirement data:', e);
          }
        }
      }

      console.log('Total lessons found:', totalLessons);

      // If no total lessons found, try alternative extraction methods
      if (totalLessons === 0 && requirementApps.length > 0) {
        console.log('Trying alternative total lessons extraction...');

        for (const reqApp of requirementApps) {
          const workText = reqApp.work || '';
          console.log('Checking requirement app work:', workText.substring(0, 200));

          // Try to find any number that could be total lessons
          const numberMatches = workText.match(/\d+/g);
          if (numberMatches) {
            console.log('Found numbers in requirement:', numberMatches);
            // Look for a reasonable total lessons number (20-50 range)
            for (const num of numberMatches) {
              const numValue = parseInt(num);
              if (numValue >= 20 && numValue <= 50) {
                totalLessons = numValue;
                console.log('Using number as total lessons:', totalLessons);
                break;
              }
            }
          }

          if (totalLessons > 0) break;
        }
      }

      // Final fallback: try to use teacher's application data if no department requirement found
      if (totalLessons === 0) {
        console.log('⚠️ No department requirement found, trying teacher application data as fallback...');
        try {
          const appData = JSON.parse(app.work || '{}');
          if (appData.totalLessonsRequirement) {
            totalLessons = parseInt(appData.totalLessonsRequirement) || 0;
            console.log('✅ Found totalLessonsRequirement in teacher application (fallback):', totalLessons);
          }
        } catch (e) {
          console.log('Could not parse teacher application work field');
        }
      }

      // If still no requirement found, return 0
      if (totalLessons === 0) {
        console.error('⚠️ NO TOTAL LESSONS REQUIREMENT FOUND!');
        console.error('Teacher ID:', app.user?.id);
        console.error('Available requirement apps:', requirementApps.length);
        console.error('Requirement apps:', requirementApps.map(r => ({
          id: r.id,
          status: r.status,
          user_id: r.user?.id,
          parameter: r.parameter?.code,
          hasTotal: r.work?.includes('totalLessons')
        })));
        console.error('Department must set a requirement first using "Set Requirements" feature.');
        return 0;  // Return 0 instead of using hardcoded value
      }

      // Additional debugging - let's also try to extract from the current parameter's maxBall
      // If maxBall is 5, and we need 25% for full points, then totalLessons should be calculated
      // For example: if videoLessons = 10 and we want 25% = 10, then totalLessons = 40
      if (totalLessons === 32 && videoLessons > 0) {
        // Try to calculate totalLessons based on the 25% rule
        // If videoLessons = 10 and we want 25%, then totalLessons should be 40
        const calculatedTotal = Math.round((videoLessons / 25) * 100);
        if (calculatedTotal >= 20 && calculatedTotal <= 50) {
          console.log('Calculated total lessons based on 25% rule:', calculatedTotal);
          totalLessons = calculatedTotal;
        }
      }

      console.log('✅ FINAL EXTRACTED TOTAL LESSONS:', totalLessons);

      // Get required percentage from parameter (default to 25 if not set)
      // Prioritize paramFromApi if available, otherwise use merged param
      const requiredPercentage = paramFromApi?.required_percentage || param?.required_percentage || 25;
      console.log('🔍 DEBUG: paramFromApi:', paramFromApi);
      console.log('🔍 DEBUG: paramFromApi.required_percentage:', paramFromApi?.required_percentage);
      console.log('🔍 DEBUG: param.required_percentage:', param?.required_percentage);
      console.log('🔍 DEBUG: Full paramFromApi object:', JSON.stringify(paramFromApi, null, 2));
      console.log('🔍 DEBUG: Full param object:', JSON.stringify(param, null, 2));
      console.log('✅ Required percentage from parameter:', requiredPercentage + '%');
      console.log('✅ Using percentage:', requiredPercentage);

      // CRITICAL: If paramFromApi is not loaded yet, wait for it!
      if (!paramFromApi) {
        console.warn('⚠️ paramFromApi is not loaded yet! Using fallback value of 25%');
        console.warn('⚠️ This may cause incorrect calculations. Please wait for parameter to load.');
      }

      // Calculate required video lessons based on the percentage from parameter
      const requiredVideoLessons = Math.ceil(totalLessons * (requiredPercentage / 100));
      console.log(`✅ Required video lessons (${requiredPercentage}%):`, requiredVideoLessons);

      // Count how many applications this teacher has submitted (each = 1 video lesson)
      // For now, we're calculating for THIS application, so count = 1
      // The backend will recalculate all applications when approving
      const videoLessonsCount = 1;  // Each application = 1 video lesson
      console.log('✅ Video lessons count (this application):', videoLessonsCount);

      // Calculate proportional points
      const proportion = videoLessonsCount / requiredVideoLessons;
      let finalPoints = Math.round(proportion * maxBall * 100) / 100;

      // Cap at max ball
      if (finalPoints > maxBall) {
        finalPoints = maxBall;
      }

      console.log('📊 CALCULATION:');
      console.log('  Total lessons requirement:', totalLessons);
      console.log(`  Required (${requiredPercentage}%):`, requiredVideoLessons);
      console.log('  Video lessons (this app):', videoLessonsCount);
      console.log('  Proportion:', proportion.toFixed(4));
      console.log('  Max ball:', maxBall);
      console.log('  Calculated points:', finalPoints);
      console.log('  Formula: (' + videoLessonsCount + ' / ' + requiredVideoLessons + ') × ' + maxBall + ' = ' + finalPoints);

      return finalPoints;
    } catch (e) {
      console.error('Error calculating KPI 1.1 points:', e);
      return 0;
    }
  };

  // Load requirement applications for KPI 1.1 to use in point calculation
  const [kpi11RequirementApps, setKpi11RequirementApps] = useState([]);

  const loadKpi11RequirementApps = async () => {
    try {
      console.log('=== LOADING REQUIREMENT APPLICATIONS ===');

      // Load ALL applications from list endpoint (includes status=6 requirements)
      // check-list only shows status=1 applications that need approval
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await Axios().get(`api/v1/application/list/?t=${timestamp}`);
      const allApps = response?.data?.applications || [];
      console.log('From list endpoint:', allApps.length, 'total apps');

      // Debug: Show all apps to see what we have
      console.log('All apps from API:', allApps.map(app => ({
        id: app.id,
        status: app.status,
        parameter: app.parameter?.code,
        user: app.user?.first_name,
        hasTotal: app.work?.includes('totalLessons'),
        workPreview: app.work?.substring(0, 50) + '...'
      })));

      // Filter for KPI 1.1 requirement applications 
      // These should be "Menga belgilangan talablar" (requirements sent to teachers)
      // Look for apps that contain totalLessons data
      const requirementApps = allApps.filter(app =>
        app.parameter?.code === '1.1' &&
        (app.work?.includes('totalLessons') ||
          app.work?.includes('"32"') ||
          app.work?.includes('32') ||
          app.work?.includes('Umumiy darslar'))
      );

      console.log('All KPI 1.1 apps:', allApps.filter(app => app.parameter?.code === '1.1').map(app => ({
        id: app.id,
        status: app.status,
        user: app.user?.first_name,
        workPreview: app.work?.substring(0, 100)
      })));

      console.log('Status=6 KPI 1.1 apps with totalLessons:', requirementApps.length);

      // Log each requirement app in detail
      requirementApps.forEach((app, index) => {
        console.log(`Requirement app ${index + 1} DETAILS:`, {
          id: app.id,
          status: app.status,
          parameter: app.parameter?.code,
          user: app.user?.first_name,
          work: app.work
        });
      });

      // If no status=6 apps, try without status filter
      if (requirementApps.length === 0) {
        const anyKpi11WithTotal = allApps.filter(app =>
          app.parameter?.code === '1.1' &&
          app.work?.includes('totalLessons')
        );
        console.log('Any KPI 1.1 apps with totalLessons (any status):', anyKpi11WithTotal.length);
        requirementApps.push(...anyKpi11WithTotal);
      }
      requirementApps.forEach((app, index) => {
        console.log(`Requirement app ${index + 1}:`, {
          id: app.id,
          status: app.status,
          parameter: app.parameter?.code,
          user: app.user?.first_name,
          work: app.work?.substring(0, 100) + '...'
        });
      });

      setKpi11RequirementApps(requirementApps);

      // If still no requirement apps found, try the teacher's endpoint
      if (requirementApps.length === 0) {
        console.log('No requirements found in check-list, trying list endpoint...');
        try {
          const response2 = await Axios().get(`api/v1/application/list/`);
          const allApps2 = response2?.data?.applications || [];
          console.log('From list endpoint:', allApps2.length, 'total apps');

          const requirementApps2 = allApps2.filter(app =>
            app.parameter?.code === '1.1' &&
            app.work?.includes('totalLessons')
          );
          console.log('KPI 1.1 apps with totalLessons from list endpoint:', requirementApps2.length);

          if (requirementApps2.length > 0) {
            setKpi11RequirementApps(requirementApps2);
          }
        } catch (e2) {
          console.error('List endpoint also failed:', e2);
        }
      }

      console.log('=== FINISHED LOADING REQUIREMENT APPLICATIONS ===');
    } catch (e) {
      console.error('Error fetching requirement applications:', e);
      setKpi11RequirementApps([]);
    }
  };

  useEffect(() => {
    // Load requirement applications when component mounts
    if (String(param?.no) === '1.1') {
      console.log('=== USEEFFECT TRIGGERED FOR KPI 1.1 ===');
      loadKpi11RequirementApps();
    }
  }, [param?.no]);

  // Reload requirement apps when myApps changes (to ensure we have the latest data)
  useEffect(() => {
    if (String(param?.no) === '1.1' && myApps.length > 0) {
      console.log('=== USEEFFECT TRIGGERED FOR REQUIREMENTS RELOAD ===');
      loadKpi11RequirementApps();
    }
  }, [param?.no, myApps]);

  // Reload requirements when page becomes visible (e.g., returning from another tab/page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && String(param?.no) === '1.1') {
        console.log('=== PAGE BECAME VISIBLE - RELOADING REQUIREMENTS ===');
        loadKpi11RequirementApps();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [param?.no]);

  // Calculate points based on authors count logic
  // If there are multiple authors and none are foreign, divide points by authors count
  // If there is at least one foreign author, don't divide points
  const calculatePointsWithAuthors = (app, maxBallString) => {
    try {
      const data = JSON.parse(app.work || '{}');
      const paramCode = String(app?.parameter?.code);

      // For KPI 2.8 (patent), use ownersCount instead of authorsCount
      let authorsCount, foreignAuthors;
      if (paramCode === '2.8') {
        authorsCount = parseInt(data.ownersCount) || 1;
        foreignAuthors = 0; // Patents don't have foreign authors concept
      } else {
        authorsCount = parseInt(data.authorsCount) || 1;
        foreignAuthors = parseInt(data.foreignAuthors || data.abroadAuthorsCount) || 0;
      }

      // Extract numeric value from maxBall string (e.g., "5 ball" -> 5)
      let maxBall = parseInt(maxBallString) || 0;

      // Special handling for KPI 1.7 - base points depend on direction
      if (paramCode === '1.7') {
        const direction = data.direction || '';
        if (direction === "O'quv qo'llanma") {
          maxBall = 3;
        } else if (direction === "Monografiya") {
          maxBall = 2;
        }
        // If direction is not set or invalid, use default maxBall
      }

      // Calculate local authors (total authors - foreign authors)
      const localAuthors = Math.max(1, authorsCount - foreignAuthors);

      // If there are multiple local authors/owners, divide points by local authors only
      if (localAuthors > 1) {
        // Divide points by the number of LOCAL authors (foreign authors don't count)
        const points = Math.round((maxBall / localAuthors) * 100) / 100;
        return points;
      }

      // Single local author/owner or only foreign authors - return full points
      return maxBall;
    } catch (e) {
      console.error('Error calculating points with authors:', e);
      return parseInt(maxBallString) || 0;
    }
  };

  // Special calculation for KPI 2.5 - fractional maqola counting
  const calculateKPI25Points = (app, approvedApps, maxBall = 3) => {
    try {
      // Filter approved 2.5 applications for this teacher
      const approved25Apps = approvedApps.filter(a =>
        String(a?.parameter?.code) === '2.5' &&
        a.status === 4 &&
        a.id !== app.id
      );

      // Calculate cumulative maqola count from previously approved apps
      let cumulativeMaqola = 0;
      for (const prevApp of approved25Apps) {
        try {
          const workData = JSON.parse(prevApp.work || '{}');
          // Frontend saves as 'authorsCount' (with 's')
          const authorCount = parseInt(workData.authorsCount || workData.authorCount) || 1;
          cumulativeMaqola += 1.0 / authorCount;
        } catch {
          cumulativeMaqola += 1.0; // Default to 1 maqola if can't parse
        }
      }

      // Calculate this application's maqola contribution
      let thisMaqola = 1.0;
      try {
        const workData = JSON.parse(app.work || '{}');
        // Frontend saves as 'authorsCount' (with 's')
        const authorCount = parseInt(workData.authorsCount || workData.authorCount) || 1;
        thisMaqola = 1.0 / authorCount;
      } catch {
        thisMaqola = 1.0; // Default to 1 maqola if can't parse
      }

      // Calculate new cumulative total
      const newCumulative = cumulativeMaqola + thisMaqola;

      console.log(`KPI 2.5: Previous maqola=${cumulativeMaqola.toFixed(2)}, This maqola=${thisMaqola.toFixed(2)}, New total=${newCumulative.toFixed(2)}`);

      // Determine points based on cumulative maqola count
      let totalPoints = 0;
      if (newCumulative < 1.0) {
        totalPoints = 0; // Below 1 maqola - no points
        console.log('KPI 2.5: Below 1 maqola threshold -> 0 points');
      } else if (newCumulative < 3.0) {
        totalPoints = 2; // Between 1-3 maqola - 2 points total
        console.log('KPI 2.5: Between 1-3 maqola -> 2 points total (will be distributed proportionally)');
      } else {
        totalPoints = 3; // 3+ maqola - 3 points total
        console.log('KPI 2.5: 3+ maqola -> 3 points total (will be distributed proportionally)');
      }

      // Return 0 - backend will calculate and distribute points proportionally
      // The totalPoints value is just for logging/understanding the threshold
      return 0;
    } catch (e) {
      console.error('Error calculating KPI 2.5 points:', e);
      return 0;
    }
  };

  // Special calculation for KPI 2.9 - received amount per participant must be >= 5,000,000
  const calculateKPI29Points = (app, maxBall) => {
    try {
      const data = JSON.parse(app.work || '{}');
      const receivedAmount = parseFloat(data.receivedAmount) || 0;
      const participantsCount = parseInt(data.participantsCount) || 1;

      // Calculate amount per participant
      const amountPerParticipant = receivedAmount / participantsCount;

      // If amount per participant is >= 5,000,000, give full points, otherwise 0
      if (amountPerParticipant >= 5000000) {
        return parseInt(maxBall) || 0;
      } else {
        return 0;
      }
    } catch (e) {
      console.error('Error calculating KPI 2.9 points:', e);
      return 0;
    }
  };

  // Special calculation for KPI 2.10 - calculate points based on grant type
  const calculateKPI210Points = (app, maxBall) => {
    try {
      const data = JSON.parse(app.work || '{}');
      const grantType = data.grantType || '';

      // Calculate points based on grant type
      let points = 0;
      if (grantType.includes('Universitetdagi')) {
        points = 3;
      } else if (grantType.includes('Boshqa muassasada')) {
        points = 1;
      }

      // Cap at max_ball
      const maxBallValue = parseInt(maxBall) || 3;
      return Math.min(points, maxBallValue);
    } catch (e) {
      console.error('Error calculating KPI 2.10 points:', e);
      return 0;
    }
  };

  const checkApp = async (appId, ball, app = null, isApproval = true) => {
    // Validate comment for both approval and rejection
    let comment = (rejectionComments[appId] || '').trim();

    // Validate comment
    if (comment.length === 0) {
      alert(isApproval ? "Tasdiqlash uchun izoh kiritish majburiy!" : "Rad etish sababini kiritish majburiy!");
      return;
    }

    if (comment.length > 80) {
      alert("Izoh 80 ta belgidan oshmasligi kerak!");
      return;
    }

    // Show confirmation dialog
    if (!window.confirm(isApproval ? "Tasdiqlashga ishonchingiz komilmi?" : "Rad etishga ishonchingiz komilmi?")) {
      return;
    }

    let rejectionComment = comment;

    try {
      // For rejection, always use ball=0 without any calculation
      let finalBall = ball;

      if (!isApproval) {
        finalBall = 0;
        console.log('Rejection: Setting ball to 0');
      } else {
        // For KPI 1.1, calculate points automatically based on video lessons percentage
        console.log('Checking app for KPI 1.1 point calculation:', {
          appCode: app?.parameter?.code,
          paramMaxBall: param?.maxBall,
          isApproval: isApproval,
          appWork: app?.work
        });

        if (app && String(app?.parameter?.code) === '1.1' && param?.maxBall) {
          console.log('Calculating KPI 1.1 points...');
          // First calculate based on video lessons percentage
          let kpi11Points = calculateKPI11Points(app, param.maxBall, kpi11RequirementApps);
          console.log('KPI 1.1 video lesson points:', kpi11Points);

          // Then adjust based on authors count (same logic as other parameters)
          finalBall = calculatePointsWithAuthorsForKPI11(app, kpi11Points);
          console.log('KPI 1.1 calculated points with authors:', finalBall);
        }
        // For KPI 2.5, use special sequential calculation (2-0-1 pattern)
        else if (app && String(app?.parameter?.code) === '2.5' && param?.maxBall) {
          finalBall = calculateKPI25Points(app, myApps, param.maxBall);
          console.log('KPI 2.5 sequential points (1st=2, 2nd=0, 3rd=1):', finalBall);
        }
        // For KPI 2.9, check if received amount per participant >= 5,000,000
        else if (app && String(app?.parameter?.code) === '2.9' && param?.maxBall) {
          finalBall = calculateKPI29Points(app, param.maxBall);
          console.log('KPI 2.9 points (received amount per participant >= 5M):', finalBall);
        }
        // For KPI 2.10, calculate points based on grant type
        else if (app && String(app?.parameter?.code) === '2.10' && param?.maxBall) {
          finalBall = calculateKPI210Points(app, param.maxBall);
          console.log('KPI 2.10 points (based on grant type):', finalBall);
        }
        // For parameters with authors/owners count, calculate points based on authors logic
        else if (app && ['1.3', '1.4', '1.6', '1.7', '1.8', '1.9', '2.4', '2.7', '2.8'].includes(String(app?.parameter?.code)) && param?.maxBall) {
          finalBall = calculatePointsWithAuthors(app, param.maxBall);
        }

        // Ensure finalBall is a valid number and cap at parameter's max_ball
        const maxBallValue = parseInt(param?.maxBall) || 5;
        finalBall = Math.max(0, Math.min(maxBallValue, Number(finalBall) || 0));
        console.log('Final ball value:', finalBall);
      }

      // Send request with comment (for both approval and rejection)
      const requestData = {
        app_id: appId,
        ball: finalBall,
        is_approval: isApproval
      };

      // Always include the comment (required for both approval and rejection)
      if (rejectionComment) {
        requestData.rejection_comment = rejectionComment;
      }

      console.log('🔍 Sending request data:', {
        ...requestData,
        isApproval: isApproval,
        paramCode: app?.parameter?.code
      });

      await Axios().post('api/v1/application/check-list/', requestData);

      // Reload the applications list to reflect the updated status
      // This will move the application from "pending" to "evaluated" status
      loadDepartmentApps();
    } catch (error) {
      console.error('Check app error:', error);
      console.error('Error details:', {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message
      });
      alert('Xatolik yuz berdi: ' + (error?.response?.data?.detail || error?.message || 'Noma\'lum xatolik'));
    }
  };

  // Check if application can be reset (within 3 days of evaluation)
  const canResetApplication = (app) => {
    // For now, always allow reset since we don't have a specific evaluation timestamp
    // TODO: Add evaluated_at field to track when department evaluated the application
    // The updated_at field changes on any update, not just evaluation
    return true;

    /* Original time-based logic (disabled until we add evaluated_at field):
    if (!app.updated_at) return false;
    
    const updatedDate = new Date(app.updated_at);
    const now = new Date();
    const diffInMs = now - updatedDate;
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
    
    // Allow reset if evaluated within last 3 days
    return diffInDays <= 3;
    */
  };

  // Handle resetting application status back to unseen
  const handleResetApplication = async (appId) => {
    if (!window.confirm("Arizani qayta ko'rib chiqish uchun baholanmagan holatga qaytarishga ishonchingiz komilmi?")) {
      return;
    }

    try {
      const response = await Axios().post(`api/v1/application/reset-status/${appId}/`);
      console.log('Reset response:', response.data);

      // Switch to baholanmagan tab and reload
      setActiveTab('baholanmagan');
      setSearchParams({ filter: 'baholanmaganlar' });

      // Small delay to ensure state updates before reloading
      setTimeout(() => {
        loadDepartmentApps();
      }, 100);

      alert("Ariza baholanmagan holatga qaytarildi");
    } catch (error) {
      console.error('Reset application error:', error);
      alert('Xatolik yuz berdi: ' + (error?.response?.data?.detail || error?.message || 'Noma\'lum xatolik'));
    }
  };

  // KPI 1.1 should NOT divide points by author count
  // Just return the base points as-is
  const calculatePointsWithAuthorsForKPI11 = (app, basePoints) => {
    console.log('KPI 1.1: Returning full points without author division:', basePoints);
    return basePoints;
  };

  const renderWorkDetails = (app) => {
    // Debug: Log the app data to see what's being received

    if (String(app?.parameter?.code) === '1.1') {
      try {
        const d = JSON.parse(app.work || '{}');
        console.log('KPI 1.1 work data for app', app.id, ':', d);
        console.log('Available keys:', Object.keys(d));

        // Check all possible field names
        const allFields = [
          { label: 'Nomi', value: d?.name || d?.nomi, key: 'name/nomi' },
          { label: 'Mualliflar soni', value: d?.authorsCount || d?.mualliflarSoni || d?.mualliflar_soni, key: 'authorsCount' },
          { label: 'Xorijlik mualliflar soni', value: d?.foreignAuthors || d?.xorijlikMualliflar || d?.xorijlik_mualliflar || d?.abroadAuthorsCount || d?.hasAbroadAuthors, key: 'foreignAuthors' },
          { label: 'Umumiy darslar soni', value: d?.totalLessons || d?.totalLessonsRequirement || d?.umumiyDarslar || d?.umumiy_darslar, key: 'totalLessons' },
          { label: 'Video darslar soni', value: d?.videoLessonsCount || d?.videoLessons || d?.videoDarslarSoni || d?.video_darslar_soni, key: 'videoLessons' },
          { label: 'Tasdiqlangan sana', value: d?.approvedDate || d?.tasdiqlanganSana || d?.tasdiqlangan_sana, key: 'approvedDate' },
          { label: 'Platforma nomi', value: d?.platformName || d?.platformaNomi || d?.platforma_nomi, key: 'platformName' },
          { label: 'Internet manzili', value: d?.url || d?.internetAddress || d?.internetManzili || d?.internet_manzili, key: 'url' }
        ];

        // Log which fields have values
        allFields.forEach(field => {
          console.log(`Field "${field.label}" (${field.key}):`, field.value);
        });

        const fields = allFields.filter(field => {
          // Keep field if value exists and is not null/undefined
          // Allow 0 as a valid value
          return field.value !== null && field.value !== undefined && field.value !== '';
        });

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  {field.label === 'Internet manzili' ? (
                    <Text
                      color="blue.600"
                      textDecoration="underline"
                      cursor="pointer"
                      onClick={() => {
                        const url = field.value.startsWith('http') ? field.value : `https://${field.value}`;
                        window.open(url, '_blank');
                      }}
                    >
                      {field.value}
                    </Text>
                  ) : (
                    <Text>{field.value}</Text>
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '1.2') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Jami talabalar soni', value: d?.totalStudents },
          { label: 'Monitoring talabalar soni', value: d?.monitoringStudents },
          { label: 'O\'rtacha o\'zlashtirish', value: d?.averageScore }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '1.3' || String(app?.parameter?.code) === '1.4') {
      try {
        const d = JSON.parse(app.work || '{}');

        // Debug: Log KPI 1.3/1.4 data

        // Find guidance document file in the files array
        const leadershipDocumentFile = app.files?.find(file =>
          // New format: uploaded with Rahbarlik_hujjati_ prefix
          file.name?.startsWith('Rahbarlik_hujjati_')
        ) ||
          app.files?.find(file =>
            // Legacy fallback: look for files with common guidance document keywords
            file.name?.toLowerCase().includes('guidance') ||
            file.name?.toLowerCase().includes('rahbarlik') ||
            file.name?.toLowerCase().includes('leadership')
          );

        const fields = [
          { label: 'Nomi va turi', value: d?.name || d?.nameAndType || d?.name_and_type || d?.['Nomi va turi'] || '' },
          { label: 'Talabaning FISHi, kursi, guruhi', value: d?.studentInfo || d?.student_info || d?.['Talabaning FISHi, kursi, guruhi'] || '' },
          { label: 'Joyi, sanasi', value: d?.location || d?.placeAndDate || d?.place_and_date || d?.['Joyi, sanasi'] || '' },
          { label: 'Egallagan o\'rni', value: d?.achievement || d?.occupiedPlace || d?.occupied_place || d?.['Egallagan o\'rni'] || '' },
          { label: 'Diplom seriyasi va raqami', value: d?.diplomaSerial || d?.diplomaInfo || d?.diploma_info || d?.['Diplom seriyasi va raqami'] || '' },
          {
            label: 'Rahbarlik hujjati',
            value: leadershipDocumentFile ? (
              <a
                href={fileHref(leadershipDocumentFile.file)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: PRIMARY, textDecoration: 'underline' }}
              >
                {leadershipDocumentFile.name}
              </a>
            ) : (d?.guidanceFile || d?.leadershipDocument || d?.leadership_document || d?.['Rahbarlik hujjati'] || '-')
          }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 1.3/1.4 data:', e);
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '1.6' || String(app?.parameter?.code) === '1.7') {
      try {
        const d = JSON.parse(app.work || '{}');

        // Find permission file in the files array
        const permissionFile = app.files?.find(file =>
          file.name?.startsWith('Ruxsatnoma_fayli_') ||
          file.name?.toLowerCase().includes('permission') ||
          file.name?.toLowerCase().includes('ruxsat') ||
          file.name?.toLowerCase().includes('grif')
        ); // Removed fallback to first file if no specific match

        const fields = [
          { label: 'Nomi', value: d?.name },
          { label: 'Nashr etilgan vaqti', value: d?.publishTime || d?.publicationTime },
          { label: 'Mualliflar soni', value: d?.authorsCount },
          { label: 'Xorijlik mualliflar soni', value: d?.foreignAuthors || '0' },
          { label: 'Tili', value: d?.language },
          { label: 'Nashr parametlari (nashriyot, sahifasi)', value: d?.publishParams || d?.publicationParams },
          { label: 'Yo\'nalishi', value: d?.direction },
          {
            label: 'Ruxsatnoma fayli',
            value: permissionFile ? (
              <a
                href={fileHref(permissionFile.file)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: PRIMARY, textDecoration: 'underline' }}
              >
                {permissionFile.name}
              </a>
            ) : 'Fayl yuklanmagan'
          }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 1.6/1.7 data:', e);
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '1.8') {
      try {
        const d = JSON.parse(app.work || '{}');

        // Find certificate file in the files array

        const certFile = app.files?.find(file =>
          file.name?.startsWith('Sertifikat_fayli_') ||
          file.name?.toLowerCase().includes('cert') ||
          file.name?.toLowerCase().includes('sertifikat') ||
          file.field_type === 'cert_file'
        ); // Removed fallback to first file

        const dataFields = [
          { label: 'Xorijiy til', value: d?.foreignLanguage },
          { label: 'Turi', value: d?.certType || d?.type },
          { label: 'Olgan daraja', value: d?.level || d?.achievedDegree },
          { label: 'Sanasi', value: d?.date }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        // Always add the certificate file field
        const certFileField = {
          label: 'Sertifikat fayli',
          value: certFile ? (
            <a
              href={fileHref(certFile.file)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: PRIMARY, textDecoration: 'underline' }}
            >
              {certFile.name}
            </a>
          ) : 'Fayl yuklanmagan'
        };

        const fields = [...dataFields, certFileField];

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 1.8 data:', e);
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '1.9') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Turi', value: d?.type },
          { label: 'Joyi', value: d?.location },  // Changed from 'place' to 'location'
          { label: 'Boshlangan sana', value: d?.startDate },
          { label: 'Yakunlangan sana', value: d?.endDate },
          { label: 'Yo\'nalish', value: d?.direction },
          { label: 'Maqsadi', value: d?.purpose }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        console.error('Error parsing KPI 1.9 data:', e);
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '2.1') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Daraja turi', value: d?.degreeType },
          { label: 'Diplom seriyasi', value: d?.diplomaSerial },
          { label: 'Diplom raqami', value: d?.diplomaNumber },
          { label: 'Berilgan sana', value: d?.diplomaDate || d?.issueDate },
          { label: 'Ixtisoslik', value: d?.specialty }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="200px 1fr" gap={3} rowGap={2}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600} fontSize="sm">{field.label}:</Text>
                  <Text fontSize="sm">{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '2.2') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Ixtisoslik shifri', value: d?.specialtyCode },
          { label: 'Ixtisoslik nomi', value: d?.specialtyName },
          { label: 'Vaqti', value: d?.date },
          { label: 'Diplom seriyasi va raqami', value: d?.diplomaSeriesNumber }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '2.4') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Jurnal nomi', value: d?.journalName },
          { label: 'Xalqaro ilmiy-texnik baza nomi', value: d?.database },
          { label: 'Maqolaning nomi', value: d?.articleName },
          { label: 'Nashr etilgan vaqti', value: d?.publishDate },
          { label: 'Maqola chop etilgan davlat', value: d?.country },
          { label: 'Mualliflar soni', value: d?.authorsCount },
          { label: 'Xorijlik mualliflar soni', value: d?.foreignAuthors },
          { label: 'Maqolaning chop etilgan tili', value: d?.articleLang },
          { label: 'Nashr parametrlari (jurnal soni, sahifasi)', value: d?.publishParams },
          { label: 'Profil manzili', value: d?.profileUrl }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '2.5') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Jurnal nomi', value: d?.journalName },
          { label: 'OAK ro\'yxati', value: d?.oakRegistry },
          { label: 'Maqolaning nomi', value: d?.articleName },
          { label: 'Nashr etilgan vaqti', value: d?.publishDate },
          { label: 'Maqola chop etilgan davlat', value: d?.country },
          { label: 'Mualliflar soni', value: d?.authorsCount },
          { label: 'Xorijlik mualliflar soni', value: d?.foreignAuthors },
          { label: 'Maqolaning chop etilgan tili', value: d?.articleLang },
          { label: 'Nashr parametrlari (jurnal soni, sahifasi)', value: d?.publishParams },
          { label: 'Maqola manzili', value: d?.articleUrl }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '2.3') {
      try {
        const d = JSON.parse(app.work || '{}');

        const fields = [
          { label: 'Shogirdining FISHi', value: d?.studentName || d?.pupilFullName },
          { label: 'Shogirdining lavozimi, shtat birligi', value: d?.studentPosition || d?.pupilPosition },
          { label: 'Ish bajarilgan tashkilot', value: d?.organization },
          { label: 'Himoya sanasi', value: d?.defenseDate },
          { label: 'Ixtisosligi shifri va nomi', value: d?.specialtyCode && d?.specialtyName ? `${d.specialtyCode} - ${d.specialtyName}` : (d?.specialtyCodeName || d?.specialtyCode || d?.specialtyName) }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '3.1') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Xorijiy davlat', value: d?.foreignCountry },
          { label: 'Xorijiy OTM nomi', value: d?.foreignUniversity },
          { label: 'Vaqti', value: d?.degreeDate },
          { label: 'Ilmiy daraja bosqichi (PhD/DSc)', value: d?.degreeLevel },
          { label: 'Diplomining seriyasi va raqami', value: d?.diplomaSerial },
          { label: 'Ixtisosligi nomi', value: d?.specialtyName }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '3.2') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Xorijiy davlat nomi', value: d?.foreignCountry },
          { label: 'Xorijiy OTM nomi', value: d?.foreignUniversity },
          { label: 'Ixtisosligi nomi', value: d?.specialtyName },
          { label: 'O\‘quv mashg‘ulot turi ', value: d?.lectureType },
          { label: 'Vaqti (boshlanishi va tugashi (kalendar))', value: d?.duration }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '3.3' || String(app?.parameter?.code) === '3.4') {
      // Handle 3.3 and 3.4 parameters
      try {
        const d = JSON.parse(app.work || '{}');

        // Map English field names to Uzbek
        const fieldLabelMap = {
          'trainingType': 'Turi',
          'trainingLocation': 'Joyi',
          'trainingDate': 'Vaqti',
          'trainingDirection': 'Yo‘nalish',
          'trainingPurpose': 'Maqsadi',
          'cooperationName': 'Nomi',
          'cooperationLocation': 'Joyi',
          'cooperationDate': 'Vaqti',
          'cooperationDirection': 'Yo‘nalish',
          'cooperationPurpose': 'Maqsadi'
        };

        const allFields = Object.entries(d).map(([key, value]) => ({
          label: fieldLabelMap[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
          value: String(value)
        }));

        const fields = allFields.filter(field => field.value && field.value !== '' && field.value !== 'null' && field.value !== 'undefined');

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '4.1') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.eventName },
          { label: 'Vaqti', value: d?.eventDate },
          { label: 'Joy', value: d?.eventLocation }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '4.3') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.activityName },
          { label: 'Vaqti', value: d?.activityDate },
          { label: 'Joy', value: d?.activityLocation }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '4.4') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.projectName },
          { label: 'Vaqti', value: d?.projectDate },
          { label: 'Joy', value: d?.projectLocation }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '4.2') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.clubName },
          { label: 'Nizomi', value: d?.clubRegulations }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '4.5') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.competitionName },
          { label: 'Turi', value: d?.competitionType },
          { label: 'Talabaning FISHi', value: d?.studentFish },
          { label: 'Joy', value: d?.competitionLocation },
          { label: 'Vaqti', value: d?.competitionDate },
          { label: 'Egallagan o\'rni', value: d?.achievement },
          { label: 'Diplom seriyasi va raqami', value: d?.diplomaSerial }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '4.6') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Mavzu', value: d?.mediaSubject },
          { label: 'Vaqti', value: d?.mediaDate },
          { label: 'OAV, ijtimoiy tarmoq', value: d?.mediaName }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '4.7') {
      try {
        const d = JSON.parse(app.work || '{}');
        const fields = [
          { label: 'Nomi', value: d?.name },
          { label: 'Vaqti', value: d?.time },
          { label: 'Hisobot', value: d?.report }
        ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if ((String(app?.parameter?.code).startsWith('2.') || String(app?.parameter?.code).startsWith('4.')) && !['2.1', '2.2', '2.3', '2.4', '2.5'].includes(String(app?.parameter?.code))) {
      // Handle other 2.x and 4.x parameters (2.6, 2.7, 2.8, 2.9, 2.10, 2.11 and 4.x)
      try {
        const d = JSON.parse(app.work || '{}');

        // Map English field names to Uzbek
        const fieldLabelMap = {
          'profileUrl': 'Profil manzili',
          'articleUrl': 'Maqola manzili',
          'url': 'Manzil',
          'articleCount': 'Maqolalar soni',
          'authorsCount': 'Mualliflar soni',
          'foreignAuthors': 'Xorijlik mualliflar soni',
          'ownersCount': 'Egalar soni',
          'participantsCount': 'Ishtirokchilar soni',
          'name': 'Nomi',
          'eventName': 'Tadbir nomi',
          'clubName': 'To\'garak nomi',
          'activityName': 'Faoliyat nomi',
          'competitionName': 'Tanlov nomi',
          'projectName': 'Loyiha nomi',
          'type': 'Turi',
          'competitionType': 'Tanlov turi',
          'date': 'Sana',
          'time': 'Vaqti',
          'place': 'Joy',
          'student': 'Talaba',
          'studentFish': 'Talaba F.I.Sh',
          'position': 'Lavozim',
          'topic': 'Mavzu',
          'media': 'OAV',
          'mediaName': 'OAV nomi',
          'regulation': 'Nizomi',
          'report': 'Hisobot',
          'diplomaSeriesNumber': 'Diplom seriyasi va raqami',
          // KPI 2.10 fields
          'grantName': 'Grant nomi',
          'grantType': 'Grant turi',
          'projectNumber': 'Loyiha raqami',
          'contractNumber': 'Shartnoma raqami',
          'projectDuration': 'Loyiha davomiyligi',
          'totalSum': 'Umumiy summa',
          // KPI 2.11 fields
          'councilName': 'Kengash nomi',
          'organizationName': 'Tashkilot nomi',
          'specialty': 'Mutaxassislik',
          'membershipDate': 'A\'zolik sanasi',
          // KPI 2.6 fields
          'database': 'Ma\'lumot bazasi',
          'hIndex': 'H-indeks',
          // KPI 2.7, 2.8, 2.9 fields
          'journalName': 'Jurnal nomi',
          'oakRegistry': 'OAK ro\'yxati',
          'articleName': 'Maqolaning nomi',
          'publishDate': 'Nashr etilgan vaqti',
          'country': 'Mamlakat',
          'articleLang': 'Maqola tili',
          'publishParams': 'Nashr parametrlari',
          'patentName': 'Patent nomi',
          'patentType': 'Patent turi',
          'patentDate': 'Patent sanasi',
          'registrationNumber': 'Ro\'yxatga olish raqami',
          'contractDate': 'Shartnoma sanasi',
          'totalAmount': 'Umumiy summa',
          'receivedAmount': 'Olingan summa'
        };

        const allFields = Object.entries(d).map(([key, value]) => {
          // Use mapped Uzbek label if available, otherwise create from key
          const label = fieldLabelMap[key] ||
            (key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'));
          return {
            label: label,
            value: String(value)
          };
        });

        const fields = allFields.filter(field => field.value && field.value !== '' && field.value !== 'null' && field.value !== 'undefined');

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    } else if (String(app?.parameter?.code) === '1.1' || String(app?.parameter?.code) === '1.5') {
      // Handle 1.1 and 1.5 parameters
      try {
        const d = JSON.parse(app.work || '{}');
        const allFields = Object.entries(d).map(([key, value]) => ({
          label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
          value: String(value)
        }));

        const fields = allFields.filter(field => field.value && field.value !== '' && field.value !== 'null' && field.value !== 'undefined');

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      } catch (e) {
        return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
      }
    }

    // Default fallback - try to parse as JSON and format nicely
    try {
      const d = JSON.parse(app.work || '{}');
      if (typeof d === 'object' && d !== null && Object.keys(d).length > 0) {
        const allFields = Object.entries(d).map(([key, value]) => ({
          label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
          value: String(value)
        }));

        const fields = allFields.filter(field => field.value && field.value !== '' && field.value !== 'null' && field.value !== 'undefined');

        return (
          <Box mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {fields.map((field, idx) => (
                <React.Fragment key={idx}>
                  <Text fontWeight={600}>{field.label}</Text>
                  <Text>{field.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      }
    } catch (e) {
      // If JSON parsing fails, fall back to original behavior
    }

    return <Text fontSize="sm" color="gray.600" mb={2}>{app?.desc || app?.work || 'Ma\'lumot yo\'q'}</Text>;
  };

  useEffect(() => {
    loadDepartmentApps();
  }, [param?.no, activeTab]);

  // Set active tab from URL params
  useEffect(() => {
    const filter = searchParams.get('filter');
    console.log('URL filter parameter:', filter);

    // Determine the desired tab based on filter parameter
    let desiredTab = 'baholanmagan'; // default

    if (filter === 'baholangan' || filter === 'baholanganlar') {
      desiredTab = 'baholangan';
    } else if (filter === 'baholanmaganlar' || filter === 'yangi') {
      desiredTab = 'baholanmagan';
    }

    // Only update state if it's different from current state
    setActiveTab(prevTab => {
      if (prevTab !== desiredTab) {
        console.log(`Changing activeTab from ${prevTab} to ${desiredTab}`);
        return desiredTab;
      }
      console.log(`activeTab already set to ${desiredTab}, no change needed`);
      return prevTab;
    });
  }, [searchParams]);

  if (!param) return <Card style={{ padding: 20 }}>Yuklanmoqda...</Card>;

  // Safety check for param object
  if (!param.no || !param.parameter) {
    console.error('Invalid param object:', param);
    return <Card style={{ padding: 20 }}>Xatolik: Parameter ma'lumotlari topilmadi</Card>;
  }

  const userPartitionName = (user?.partition?.name || '').toLowerCase();
  const isTT = userPartitionName.includes('tarmoq texnologiyalari');
  const isTS = userPartitionName.includes("ta'lim sifati") || userPartitionName.includes("talim sifati") || userPartitionName.includes("ta'lim sifati");
  const isOV = userPartitionName.includes("o'quv") || userPartitionName.includes("oquv") || userPartitionName.includes("o'quv") || userPartitionName.includes("o'quv bo'limi") || userPartitionName.includes("oquv bo'limi");
  const isMN = userPartitionName.includes("ma'naviyat") || userPartitionName.includes("ma'naviyat") || userPartitionName.includes("manaviyat") || userPartitionName.includes("ma`naviyat");
  const isXL = userPartitionName.includes("xalqaro hamkorlik");
  const isIL = userPartitionName.includes("ilmiy bo'lim") || userPartitionName.includes("ilmiy tadqiqotlar bo'limi");
  const isMK = userPartitionName.includes("marketing");

  // Helper function to get department-specific background color
  const getDepartmentStatusBg = (app) => {
    const statusText = getDepartmentStatusText(app);
    if (statusText === 'Tasdiqlandi') return 'rgba(46, 204, 113, 0.15)'; // Green for approved
    if (statusText === 'Rad etildi') return 'rgba(231, 76, 60, 0.15)'; // Red for rejected
    return 'rgba(241, 196, 15, 0.25)'; // Yellow for in progress
  };

  // Helper function to get department-specific status text
  const getDepartmentStatusText = (app) => {
    const code = app?.parameter?.code;
    const userPartitionName = (user?.partition?.name || '').toLowerCase();
    const isTT = userPartitionName.includes('tarmoq texnologiyalari');
    const isTS = userPartitionName.includes("ta'lim sifati") || userPartitionName.includes("talim sifati");
    const isOV = userPartitionName.includes("o'quv") || userPartitionName.includes("oquv");
    const isMN = userPartitionName.includes("ma'naviyat") || userPartitionName.includes("manaviyat");
    const isXL = userPartitionName.includes("xalqaro hamkorlik");
    const isIL = userPartitionName.includes("ilmiy bo'lim");

    let deptStatus = null;

    // Get department-specific status
    if (code === '1.1') {
      if (isTT) deptStatus = app.tt_status;
      else if (isTS) deptStatus = app.ts_status;
      else if (isOV) deptStatus = app.ov_status;
    } else if (code === '1.3' || code === '1.4') {
      if (isMN) deptStatus = app.mn_status;
      else if (isOV) deptStatus = app.ov_status;
    } else if (code === '3.2' || code === '3.3') {
      if (isOV) deptStatus = app.ov_status;
      else if (isXL) deptStatus = app.tt_status;
      else if (isIL) deptStatus = app.ts_status;
    } else if (code === '3.4' || code === '3.1' || code === '2.1') {
      if (isXL) deptStatus = app.tt_status;
      else if (isIL) deptStatus = app.ts_status;
    } else {
      // For single-department KPIs, use global status
      deptStatus = app.status === 4 ? 1 : app.status === 5 ? 2 : null;
    }

    // Convert department status to text
    if (deptStatus === 1) return 'Tasdiqlandi';
    if (deptStatus === 2) return 'Rad etildi';
    return 'Jarayonda';
  };

  // Helper function to check if app is evaluated by current department
  const isEvaluatedByCurrentDept = (app) => {
    const code = app?.parameter?.code;

    if (code === '1.1') {
      if (isTT && (app.tt_status === 1 || app.tt_status === 2)) return true;
      if (isTS && (app.ts_status === 1 || app.ts_status === 2)) return true;
      if (isOV && (app.ov_status === 1 || app.ov_status === 2)) return true;
      return false;
    }

    if (code === '1.3' || code === '1.4') {
      if (isMN && (app.mn_status === 1 || app.mn_status === 2)) return true;
      if (isOV && (app.ov_status === 1 || app.ov_status === 2)) return true;
      return false;
    }

    if (code === '3.2' || code === '3.3') {
      if (isOV && (app.ov_status === 1 || app.ov_status === 2)) return true;
      if (isXL && (app.tt_status === 1 || app.tt_status === 2)) return true;
      if (isIL && (app.ts_status === 1 || app.ts_status === 2)) return true;
      return false;
    }

    if (code === '3.4') {
      if (isXL && (app.tt_status === 1 || app.tt_status === 2)) return true;
      if (isIL && (app.ts_status === 1 || app.ts_status === 2)) return true;
      return false;
    }

    if (code === '3.1' || code === '2.1') {
      if (isXL && (app.tt_status === 1 || app.tt_status === 2)) return true;
      if (isIL && (app.ts_status === 1 || app.ts_status === 2)) return true;
      return false;
    }



    if (code.startsWith('4.')) {
      if (isMN && (app.status !== 1 && app.status !== 2 && app.status !== 3)) return true;
      return false;
    }

    // For regular single-department applications
    return app.status !== 1 && app.status !== 2 && app.status !== 3;
  };

  // Filter applications that have NOT been processed by the current department (pending)
  const pendingApps = myApps.filter(app => {
    // Skip requirement applications (status=6) - these are not teacher applications
    if (app.status === 6) {
      return false;
    }

    // For multi-department approvals, check if the current department has processed it
    const code = app?.parameter?.code;
    const userPartitionName = (user?.partition?.name || '').toLowerCase();

    // Check if user's department matches the required departments for multi-department approvals
    const isTT = userPartitionName.includes('tarmoq texnologiyalari');
    const isTS = userPartitionName.includes("ta'lim sifati") || userPartitionName.includes("talim sifati") || userPartitionName.includes("ta’lim sifati");
    const isOV = userPartitionName.includes("o'quv") || userPartitionName.includes("oquv") || userPartitionName.includes("o‘quv") || userPartitionName.includes("o'quv bo'limi") || userPartitionName.includes("oquv bo'limi");
    const isMN = userPartitionName.includes("ma'naviyat") || userPartitionName.includes("ma’naviyat") || userPartitionName.includes("manaviyat") || userPartitionName.includes("ma`naviyat");
    const isXL = userPartitionName.includes("xalqaro hamkorlik");
    const isIL = userPartitionName.includes("ilmiy bo'lim") || userPartitionName.includes("ilmiy tadqiqotlar bo'limi");

    if (code === '1.1') {
      // For KPI 1.1, check if current department has approved/rejected it
      // Status 0 = not seen, -1 = seen, 1 = approved, 2 = rejected
      if (isTT && (app.tt_status === 1 || app.tt_status === 2)) return false; // Approved/Rejected by TT
      if (isTS && (app.ts_status === 1 || app.ts_status === 2)) return false; // Approved/Rejected by TS
      if (isOV && (app.ov_status === 1 || app.ov_status === 2)) return false; // Approved/Rejected by OV
      return true; // Not yet approved/rejected by current department
    }

    if (code === '1.3' || code === '1.4') {
      // For KPI 1.3/1.4, check if current department has approved/rejected it
      if (isMN && (app.mn_status === 1 || app.mn_status === 2)) return false; // Approved/Rejected by MN
      if (isOV && (app.ov_status === 1 || app.ov_status === 2)) return false; // Approved/Rejected by OV
      return true; // Not yet approved/rejected by current department
    }

    if (code === '3.2' || code === '3.3') {
      // For KPI 3.2/3.3, check if current department has approved/rejected it
      if (isOV && (app.ov_status === 1 || app.ov_status === 2)) return false; // Approved/Rejected by OV
      if (isXL && (app.tt_status === 1 || app.tt_status === 2)) return false; // Approved/Rejected by XL (using tt_status)
      if (isIL && (app.ts_status === 1 || app.ts_status === 2)) return false; // Approved/Rejected by IL (using ts_status)
      return true; // Not yet approved/rejected by current department
    }

    if (code === '3.4') {
      // For KPI 3.4, check if current department has approved/rejected it
      if (isXL && (app.tt_status === 1 || app.tt_status === 2)) return false; // Approved/Rejected by XL (using tt_status)
      if (isIL && (app.ts_status === 1 || app.ts_status === 2)) return false; // Approved/Rejected by IL (using ts_status)
      return true; // Not yet approved/rejected by current department
    }

    if (code === '3.1' || code === '2.1') {
      // For KPI 3.1/2.1, check if current department has approved/rejected it
      if (isXL && (app.tt_status === 1 || app.tt_status === 2)) return false; // Approved/Rejected by XL (using tt_status)
      if (isIL && (app.ts_status === 1 || app.ts_status === 2)) return false; // Approved/Rejected by IL (using ts_status)
      return true; // Not yet approved/rejected by current department
    }



    // For 4.x parameters, check if current department is Ma'naviyat bo'limi
    if (code.startsWith('4.')) {
      // 4.x parameters should only be visible to Ma'naviyat bo'limi
      if (isMN) {
        // 4.7 is now single-department like other 4.x parameters
        if (code === '4.7') {
          if (app.status !== 1 && app.status !== 2 && app.status !== 3) return false; // Processed by Ma'naviyat
          return true; // Not processed by Ma'naviyat
        }
        // For other 4.x parameters, check if processed by Ma'naviyat
        if (app.status !== 1 && app.status !== 2 && app.status !== 3) return false; // Processed by Ma'naviyat
        return true; // Not processed by Ma'naviyat
      }
      return false; // Not Ma'naviyat bo'limi
    }

    // For regular applications, show all unevaluated (status 1, 2, or 3)
    return app.status === 1 || app.status === 2 || app.status === 3;
  });

  // Filter applications that HAVE been processed by the current department (evaluated)
  const evaluatedApps = myApps.filter(app => {
    // Skip requirement applications (status=6) - these are not teacher applications
    if (app.status === 6) {
      return false;
    }

    const code = app?.parameter?.code;
    const userPartitionName = (user?.partition?.name || '').toLowerCase();

    // Check if user's department matches the required departments for multi-department approvals
    // Use the same comprehensive logic as the backend to handle encoding issues
    const isTT = userPartitionName.includes('tarmoq texnologiyalari');
    const isTS = userPartitionName.includes("ta'lim sifati") || userPartitionName.includes("talim sifati");
    // More comprehensive O'quv bo'limi detection - handle all possible variations
    const isOV = userPartitionName.includes("o'quv") ||
      userPartitionName.includes("oquv") ||
      userPartitionName.includes("oâ€˜quv") ||
      userPartitionName.includes("o'quv bo'limi") ||
      userPartitionName.includes("oquv bo'limi") ||
      userPartitionName === "o'quv bo'limi" ||
      userPartitionName.includes("o'quv-uslubiy") ||
      userPartitionName.includes("oquv-uslubiy") ||
      user?.partition?.name?.toLowerCase().includes("o'quv") ||
      user?.partition?.name?.toLowerCase().includes("oquv");
    const isMN = userPartitionName.includes("ma'naviyat") || userPartitionName.includes("manaviyat") || userPartitionName.includes("maâ€™naviyat") || userPartitionName.includes("ma`naviyat") || userPartitionName.includes("ma'naviyat bo'limi");
    const isXL = userPartitionName.includes("xalqaro hamkorlik");
    const isIL = userPartitionName.includes("ilmiy bo'lim");

    if (code === '1.1') {
      if (isTT) return app.tt_status === 1 || app.tt_status === 2; // Approved/Rejected by TT
      if (isTS) return app.ts_status === 1 || app.ts_status === 2; // Approved/Rejected by TS
      if (isOV) return app.ov_status === 1 || app.ov_status === 2; // Approved/Rejected by OV
      return false;
    }

    if (code === '1.3' || code === '1.4') {
      if (isMN) return app.mn_status === 1 || app.mn_status === 2; // Approved/Rejected by MN
      if (isOV) return app.ov_status === 1 || app.ov_status === 2; // Approved/Rejected by OV
      return false;
    }

    if (code === '3.2' || code === '3.3') {
      if (isOV) return app.ov_status === 1 || app.ov_status === 2; // Approved/Rejected by OV
      if (isXL) return app.tt_status === 1 || app.tt_status === 2; // Approved/Rejected by XL
      if (isIL) return app.ts_status === 1 || app.ts_status === 2; // Approved/Rejected by IL
      return false;
    }

    if (code === '3.4') {
      if (isXL) return app.tt_status === 1 || app.tt_status === 2; // Approved/Rejected by XL
      if (isIL) return app.ts_status === 1 || app.ts_status === 2; // Approved/Rejected by IL
      return false;
    }

    if (code === '3.1' || code === '2.1') {
      if (isXL) return app.tt_status === 1 || app.tt_status === 2; // Approved/Rejected by XL
      if (isIL) return app.ts_status === 1 || app.ts_status === 2; // Approved/Rejected by IL
      return false;
    }

    // For single-department KPIs, check department-specific status fields
    if ((code === '1.2' || code === '1.5') && isTS) {
      return app.ts_status === 1 || app.ts_status === 2; // Approved/Rejected by TS
    }
    if (code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9') {
      // For 1.6-1.9, these are assigned to O'quv bo'limi, so check ov_status regardless of department name
      const result = app.ov_status === 1 || app.ov_status === 2; // Approved/Rejected by OV
      // Debug for parameter 1.6
      if (code === '1.6') {
        console.log(`🔍 Parameter 1.6 EVALUATED FILTER (SIMPLIFIED):`, {
          appId: app.id,
          code,
          ov_status: app.ov_status,
          result,
          logic: 'Checking ov_status directly without department name matching'
        });
      }
      return result;
    }
    if (code.startsWith('2.') && isIL) {
      return app.ts_status === 1 || app.ts_status === 2; // Approved/Rejected by IL (uses ts_status)
    }
    if (code.startsWith('4.') && isMN) {
      return app.mn_status === 1 || app.mn_status === 2; // Approved/Rejected by MN
    }

    // Fallback: check global status for other cases
    return app.status === 4 || app.status === 5; // Approved or Rejected
  });

  // Use appropriate filtered list based on active tab
  const displayApps = activeTab === 'baholangan' ? evaluatedApps : pendingApps;

  // Debug for parameter 1.6
  if (String(param.no) === '1.6') {
    console.log(`📊 Parameter 1.6 DISPLAY LOGIC:`, {
      activeTab,
      myAppsCount: myApps.length,
      pendingAppsCount: pendingApps.length,
      evaluatedAppsCount: evaluatedApps.length,
      displayAppsCount: displayApps.length,
      usingEvaluated: activeTab === 'baholangan'
    });
  }

  return (
    <Layout>
      <Box p={4}>
        <Button onClick={() => navigate(-1)} mb={3} color="white" bg={PRIMARY} _hover={{ bg: PRIMARY }}>
          <ArrowBackIcon />
          <span style={{ marginLeft: 5 }}>Orqaga</span>
        </Button>

        <Box mb={4}>
          <Heading size="sm" mb={3}>{param.no} - {param.parameter}</Heading>
          <Box display="flex" flexWrap="wrap" gap={3} alignItems="center">
            {(['1.2', '1.5', '2.1', '2.2'].includes(String(param.no))) ? (
              <Button
                size="sm"
                onClick={() => navigate(`/kafedra-distribution/${param.no}`)}
                color="white"
                bg="#133654"
                _hover={{ bg: '#0f2a42' }}
                whiteSpace="nowrap"
              >
                Kafedra orqali ball berish
              </Button>
            ) : null}
            {String(param.no) === '1.1' && (() => {
              const userPartitionName = (user?.partition?.name || '').toLowerCase();
              const isOV = userPartitionName.includes("o'quv") || userPartitionName.includes("oquv");
              return isOV;
            })() && (
                <Button
                  size="sm"
                  onClick={() => navigate(`/requirements-by-parameter/${param.no}`)}
                  color="white"
                  bg="#133654"
                  _hover={{ bg: '#0f2a42' }}
                  whiteSpace="nowrap"
                >
                  O'qituvchilarni {param.no} - parameter bo'yicha talablarini belgilash
                </Button>
              )}
          </Box>
        </Box>

        <Card style={{ padding: 16, marginBottom: 12 }}>
          <Text fontWeight={600} mb={2}>Mexanizm</Text>
          <Text whiteSpace="pre-wrap">
            {(() => {
              // For KPI 1.1, replace hardcoded 25% with actual required_percentage
              if (param?.no === '1.1' && paramFromApi?.required_percentage) {
                const mechanismText = param.mechanism || '';
                // Replace "25%" with actual percentage
                return mechanismText.replace(/25%/g, `${paramFromApi?.required_percentage || 25}%`);
              }
              return param.mechanism || '';
            })()}
          </Text>
          <Text mt={3}><b>Maks. ball:</b> {param.maxBall}</Text>
          {param?.no?.startsWith('4.') && paramFromApi?.max_applications_count && paramFromApi.max_applications_count > 1 && (
            <Text mt={2} color="blue.600" fontSize="sm">
              <b>{(parseFloat(param.maxBall) / paramFromApi.max_applications_count).toFixed(2)} ball</b> (maksimal {paramFromApi.max_applications_count} ta ariza)
            </Text>
          )}
        </Card>

        <Card style={{ padding: 20 }}>
          <Heading size="sm" mb={3}>
            {activeTab === 'baholangan' ? 'Baholangan arizalar' : "O'qituvchilar arizalari"} ({displayApps.length})
          </Heading>
          <Text fontSize="sm" color="gray.600">
            {loadingList
              ? "Yuklanmoqda..."
              : activeTab === 'baholangan'
                ? `Baholangan arizalar (${displayApps.length} ta)`
                : displayApps.length === 1
                  ? `${displayApps[0].user?.username || (displayApps[0].user?.first_name && displayApps[0].user?.last_name ? displayApps[0].user.first_name + ' ' + displayApps[0].user.last_name : '') || displayApps[0].username || 'O\'qituvchi'} arizasi`
                  : `O'qituvchilar arizalari (${displayApps.length} ta)`
            }
          </Text>
          <Box>
            {loadingList ? (
              <Text>Yuklanmoqda...</Text>
            ) : displayApps.length === 0 ? (
              <Text>Bu parametr bo'yicha arizalar topilmadi</Text>
            ) : (
              displayApps.map((app, index) => (
                <Box key={app.id} mb={3} p={{ base: 2, md: 3 }} border="1px solid #e2e8f0" borderRadius="md" bg={getDepartmentStatusBg(app)}>
                  <Box display="flex" flexDirection={{ base: "column", md: "row" }} justifyContent="space-between" alignItems={{ base: "stretch", md: "start" }} gap={2}>
                    <Box flex={1}>
                      <Box
                        display="inline-block"
                        bg="#133654"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontWeight={600}
                        mb={2}
                        fontSize="sm"
                      >
                        {app.user?.first_name && app.user?.last_name
                          ? `${app.user.first_name} ${app.user.middle_name ? app.user.middle_name + ' ' : ''}${app.user.last_name}`
                          : app.user?.username || 'Noma\'lum foydalanuvchi'}
                      </Box>

                      {renderWorkDetails(app)}

                      {app.files && app.files.length > 0 && String(app?.parameter?.code) !== '1.8' && (
                        <Box mb={2}>
                          <Text fontSize="sm" fontWeight={600}>Fayllar:</Text>
                          {app.files.map((file, idx) => (
                            <Box key={idx}>
                              <a href={fileHref(file.file)} target="_blank" rel="noopener noreferrer" style={{ color: PRIMARY, textDecoration: 'underline' }}>
                                {file.name || `Fayl ${idx + 1}`}
                              </a>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Show evaluation status when viewing evaluated applications */}
                    {activeTab === 'baholangan' && (() => {
                      // Get the comment for this specific department
                      const departmentName = user?.partition?.name?.toLowerCase() || "";
                      const isTT = departmentName.includes("tarmoq texnologiyalari");
                      const isTS = departmentName.includes("ta'lim sifati") || departmentName.includes("talim sifati");
                      const isOV = departmentName.includes("o'quv") || departmentName.includes("oquv");
                      const isMN = departmentName.includes("ma'naviyat") || departmentName.includes("manaviyat");
                      const isIL = departmentName.includes("ilmiy bo'lim");
                      const isMK = departmentName.includes("marketing");
                      const isXL = departmentName.includes("xalqaro hamkorlik");

                      let deptComment = '';
                      if (isTT) deptComment = app.tt_comment || '';
                      else if (isTS) deptComment = app.ts_comment || '';
                      else if (isOV) deptComment = app.ov_comment || '';
                      else if (isMN) deptComment = app.mn_comment || '';
                      else if (isMK) deptComment = app.mn_comment || ''; // Marketing uses mn_comment
                      else if (isIL) deptComment = app.ts_comment || ''; // IL uses ts_comment
                      else if (isXL) {
                        // XL uses tt_comment (new data only)
                        // Don't show old ov_comment data to avoid confusion with O'quv bo'limi
                        deptComment = app.tt_comment || '';
                      }

                      return (
                        <Box ml={4} textAlign="right">
                          <Box>
                            <Text fontSize="sm" color="gray.600" display="inline">Holat: </Text>
                            <Text
                              fontSize="sm"
                              fontWeight={600}
                              color={getDepartmentStatusText(app) === 'Tasdiqlandi' ? 'green.600' : getDepartmentStatusText(app) === 'Rad etildi' ? 'red.600' : 'orange.600'}
                              display="inline"
                            >
                              {getDepartmentStatusText(app)}
                            </Text>
                          </Box>
                          {app.ball !== null && app.ball !== undefined && !(String(app?.parameter?.code) === '2.5' && app.ball === 0) && (
                            <Box mt={1}>
                              <Text fontSize="sm" color="blue.600" fontWeight={600}>
                                Ball: {app.ball}
                              </Text>
                            </Box>
                          )}
                          {deptComment && (
                            <Box mt={1}>
                              <Text
                                fontSize="sm"
                                color={getDepartmentStatusText(app) === 'Rad etildi' ? 'red.600' : 'green.600'}
                                fontStyle="italic"
                              >
                                {getDepartmentStatusText(app) === 'Rad etildi' ? 'Sabab' : 'Izoh'}: {deptComment}
                              </Text>
                            </Box>
                          )}
                          {/* Tahrirlash button for evaluated applications - only show within 3 days */}
                          {canResetApplication(app) && (
                            <Box mt={2}>
                              <Button
                                size="sm"
                                colorScheme="orange"
                                onClick={() => handleResetApplication(app.id)}
                              >
                                Tahrirlash
                              </Button>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Department approval buttons - hide for KPI 2.1 and 2.2, and hide when viewing evaluated applications */}
                    {(() => {
                      const paramCode = String(app?.parameter?.code);
                      const isNotExcluded = !['2.1', '2.2'].includes(paramCode);
                      const isCorrectTab = activeTab === 'baholanmagan';
                      const isPendingStatus = app.status === 1 || app.status === 2 || app.status === 3;

                      // Only show buttons in 'baholanmagan' tab, for non-excluded KPIs, with pending status
                      // Status 1 = Created, Status 2 = Seen by department, Status 3 = Seen by Prorector
                      // All these statuses should show approve/reject buttons
                      const shouldShowButtons = isNotExcluded && isCorrectTab && isPendingStatus;

                      console.log('🔍 DETAILED Approval buttons check:', {
                        appId: app.id,
                        paramCode,
                        activeTab,
                        appStatus: app.status,
                        isNotExcluded,
                        isCorrectTab,
                        isPendingStatus,
                        shouldShowButtons,
                        urlFilter: new URLSearchParams(window.location.search).get('filter')
                      });

                      // Special case for KPI 1.6 debugging
                      if (paramCode === '1.6') {
                        console.log('🚨 KPI 1.6 SPECIAL CHECK:', {
                          appId: app.id,
                          status: app.status,
                          willShow: shouldShowButtons
                        });
                      }

                      if (shouldShowButtons) {
                        console.log('🎯 RENDERING APPROVAL BUTTONS for app', app.id);
                      }

                      return shouldShowButtons;
                    })() && (
                        <Box display="flex" gap={2} ml={4} alignItems="center">
                          {(() => {
                            const paramCode = String(app?.parameter?.code);
                            // Get max ball from parameter, default to 5 if not set
                            const maxBallFromParam = parseInt(param?.maxBall) || 5;
                            let ballValue = maxBallFromParam; // Use parameter's max ball as default
                            let ballDisplay = maxBallFromParam.toString();

                            // Calculate ball value for KPI 1.1 regardless of status (for multi-department approval)
                            if (paramCode === '1.1' && param?.maxBall) {
                              console.log('=== CALCULATING KPI 1.1 POINTS ===');
                              console.log('Full app object:', app);
                              console.log('App work field:', app.work);
                              console.log('Requirement apps available:', kpi11RequirementApps);
                              console.log('BEFORE calculation - ballValue:', ballValue, 'ballDisplay:', ballDisplay);
                              ballValue = calculateKPI11Points(app, param.maxBall, kpi11RequirementApps);
                              console.log('AFTER calculation - returned value:', ballValue);
                              ballDisplay = ballValue.toString();
                              console.log('AFTER setting display - ballValue:', ballValue, 'ballDisplay:', ballDisplay);
                              console.log('Calculated KPI 1.1 points:', ballValue);
                              console.log('=== END KPI 1.1 POINTS CALCULATION ===');
                            }

                            // Only show approval buttons if status is 1 OR if it's a multi-department parameter
                            // and this department hasn't responded yet
                            let shouldShowButtons = app.status === 1;



                            console.log('🔍 Status check for approval buttons:', {
                              appId: app.id,
                              paramCode,
                              status: app.status,
                              mn_status: app.mn_status,
                              ts_status: app.ts_status,
                              isMK,
                              isIL,
                              shouldShowButtons
                            });

                            if (!shouldShowButtons) {
                              // For KPI 1.1, still show the ball value even if status is not 1
                              if (paramCode === '1.1') {
                                return (
                                  <Text fontSize="sm" color="blue.600" fontWeight={600}>
                                    Ball: {ballDisplay}
                                  </Text>
                                );
                              }
                              return null;
                            }

                            // Continue with other KPI calculations for status === 1
                            // Skip other calculations if we already calculated for KPI 1.1
                            if (paramCode !== '1.1' && paramCode === '1.2') {
                              // Automatic calculation for KPI 1.2 based on average performance
                              try {
                                const workData = JSON.parse(app?.work || '{}');
                                if (!workData || typeof workData !== 'object') {
                                  console.error('Invalid work data for KPI 1.2');
                                  ballValue = 0;
                                  ballDisplay = '0';
                                } else {
                                  const avgPerformance = parseFloat(workData.averagePerformance || workData.averageScore || 0);

                                  // Convert to percentage (if it's on 10-point scale, multiply by 10)
                                  const avgPercentage = avgPerformance <= 10 ? avgPerformance * 10 : avgPerformance;

                                  // Calculate points: 90-100% → 8, 60-80% → 6, 30-50% → 3
                                  if (avgPercentage >= 90) {
                                    ballValue = 8;
                                  } else if (avgPercentage >= 60) {
                                    ballValue = 6;
                                  } else if (avgPercentage >= 30) {
                                    ballValue = 3;
                                  } else {
                                    ballValue = 0;
                                  }

                                  ballDisplay = `${ballValue} (${avgPercentage}%)`;
                                  console.log(`KPI 1.2: avg=${avgPerformance}, percentage=${avgPercentage}%, points=${ballValue}`);
                                }
                              } catch (e) {
                                console.error('Error calculating KPI 1.2 points:', e);
                                ballValue = parseInt(param?.maxBall) || 8;
                                ballDisplay = ballValue.toString();
                              }
                            } else if (paramCode === '2.3') {
                              // KPI 2.3: Calculate based on student position
                              try {
                                const workData = JSON.parse(app?.work || '{}');
                                if (!workData || typeof workData !== 'object') {
                                  console.error('Invalid work data for KPI 2.3');
                                  ballValue = 0;
                                  ballDisplay = '0';
                                } else {
                                  const studentPosition = workData.studentPosition || workData.pupilPosition || '';

                                  if (studentPosition.includes('Fan doktori')) {
                                    ballValue = 2;
                                  } else if (studentPosition.includes('Falsafa doktori')) {
                                    ballValue = 1;
                                  } else {
                                    ballValue = 0;
                                  }
                                  ballDisplay = `${ballValue} (${studentPosition})`;
                                  console.log(`KPI 2.3: position=${studentPosition}, points=${ballValue}`);
                                }
                              } catch (e) {
                                console.error('Error calculating KPI 2.3 points:', e);
                                ballValue = 0;
                                ballDisplay = '0';
                              }
                            } else if (paramCode === '2.6') {
                              // KPI 2.6: Calculate based on database and H-index
                              try {
                                const workData = JSON.parse(app?.work || '{}');
                                if (!workData || typeof workData !== 'object') {
                                  console.error('Invalid work data for KPI 2.6');
                                  ballValue = 0;
                                  ballDisplay = '0';
                                } else {
                                  const database = workData.database || '';
                                  const hIndex = parseInt(workData.hIndex) || 0;

                                  if (database === 'Scopus' || database === 'Web of Science') {
                                    // Scopus/Web of Science: H-index >= 3 → 4 points, H-index 1-2 → 3 points
                                    if (hIndex >= 3) {
                                      ballValue = 4;
                                    } else if (hIndex >= 1) {
                                      ballValue = 3;
                                    } else {
                                      ballValue = 0;
                                    }
                                  } else if (database === 'RG' || database === 'GS') {
                                    // RG/GS: 1 point
                                    ballValue = 1;
                                  } else {
                                    ballValue = 0;
                                  }
                                  ballDisplay = `${ballValue} (${database}, H=${hIndex})`;
                                  console.log(`KPI 2.6: database=${database}, H-index=${hIndex}, points=${ballValue}`);
                                }
                              } catch (e) {
                                console.error('Error calculating KPI 2.6 points:', e);
                                ballValue = 0;
                                ballDisplay = '0';
                              }
                            } else if (paramCode === '2.10') {
                              // KPI 2.10: Calculate points based on grant type
                              try {
                                const workData = JSON.parse(app?.work || '{}');
                                if (!workData || typeof workData !== 'object') {
                                  console.error('Invalid work data for KPI 2.10');
                                  ballValue = 0;
                                  ballDisplay = '0';
                                } else {
                                  const grantType = workData.grantType || '';

                                  if (grantType.includes('Universitetdagi')) {
                                    ballValue = 3;
                                  } else if (grantType.includes('Boshqa muassasada')) {
                                    ballValue = 1;
                                  } else {
                                    ballValue = 0;
                                  }
                                  ballDisplay = `${ballValue} (${grantType})`;
                                }
                              } catch (e) {
                                console.error('Error calculating KPI 2.10 points:', e);
                                ballValue = 0;
                                ballDisplay = '0';
                              }
                            } else if (paramCode === '2.11') {
                              // KPI 2.11: Calculate points based on position
                              try {
                                const workData = JSON.parse(app?.work || '{}');
                                if (!workData || typeof workData !== 'object') {
                                  console.error('Invalid work data for KPI 2.11');
                                  ballValue = 0;
                                  ballDisplay = '0';
                                } else {
                                  const position = workData.position || '';

                                  if (position.includes("IK va ITK a'zoligi") || position.includes("IK va ITK azoligi")) {
                                    ballValue = 2;
                                  } else if (position.includes("IS a'zolik") || position.includes("IS azolik")) {
                                    ballValue = 1;
                                  } else {
                                    ballValue = 0;
                                  }
                                  ballDisplay = `${ballValue} (${position})`;
                                }
                              } catch (e) {
                                console.error('Error calculating KPI 2.11 points:', e);
                                ballValue = 0;
                                ballDisplay = '0';
                              }
                            } else if (paramCode === '2.5' && param?.maxBall) {
                              // KPI 2.5: Fractional maqola calculation
                              ballValue = calculateKPI25Points(app, myApps, param.maxBall);
                              // Don't display ball value for KPI 2.5 - it will be calculated after approval
                              ballDisplay = null;
                            } else if (paramCode === '2.9' && param?.maxBall) {
                              // KPI 2.9: Check if received amount per participant >= 5,000,000
                              ballValue = calculateKPI29Points(app, param.maxBall);
                              try {
                                const data = JSON.parse(app.work || '{}');
                                const receivedAmount = parseFloat(data.receivedAmount) || 0;
                                const participantsCount = parseInt(data.participantsCount) || 1;
                                const amountPerParticipant = receivedAmount / participantsCount;
                                ballDisplay = `${ballValue} (${amountPerParticipant.toLocaleString()} so'm/kishi)`;
                              } catch {
                                ballDisplay = ballValue.toString();
                              }
                            } else if (paramCode === '2.10' && param?.maxBall) {
                              // KPI 2.10: Calculate points based on grant type
                              ballValue = calculateKPI210Points(app, param.maxBall);
                              try {
                                const data = JSON.parse(app.work || '{}');
                                const grantType = data.grantType || '';
                                ballDisplay = `${ballValue} (${grantType})`;
                              } catch {
                                ballDisplay = ballValue.toString();
                              }
                            } else if (['1.6', '1.7', '2.4', '2.7', '2.8'].includes(paramCode) && param?.maxBall) {
                              ballValue = calculatePointsWithAuthors(app, param.maxBall);
                              ballDisplay = ballValue.toString();
                            } else if (paramCode.startsWith('4.') && paramFromApi?.max_applications_count && paramFromApi.max_applications_count > 1) {
                              // KPI 4.x: Calculate points per application based on max_applications_count
                              const maxBall = parseInt(param?.maxBall) || 3;
                              const pointsPerApp = maxBall / paramFromApi.max_applications_count;
                              ballValue = pointsPerApp;
                              ballDisplay = pointsPerApp.toFixed(2);
                              console.log(`KPI ${paramCode}: max_ball=${maxBall}, max_apps=${paramFromApi.max_applications_count}, points_per_app=${pointsPerApp}`);
                            } else if (paramCode !== '1.1' && param?.maxBall) {
                              // Don't override KPI 1.1 calculated value
                              ballValue = parseInt(param?.maxBall) || 5;
                              ballDisplay = ballValue.toString();
                            }

                            return (
                              <Box display="flex" flexDirection="column" gap={2} width="100%">
                                <Box display="flex" gap={2} alignItems="center">
                                  {ballDisplay !== null && (
                                    <Text fontSize="sm" color="blue.600" fontWeight={600}>
                                      Ball: {ballDisplay}
                                    </Text>
                                  )}
                                  <Button
                                    size="sm"
                                    colorScheme="green"
                                    bg="#25D366"
                                    color="white"
                                    _hover={{ bg: "#128C7E" }}
                                    _active={{ bg: "#075E54" }}
                                    onClick={() => {
                                      // Recalculate points before approval
                                      let finalBall = ballValue;
                                      if (String(app?.parameter?.code) === '1.1' && param?.maxBall) {
                                        finalBall = calculateKPI11Points(app, param.maxBall, kpi11RequirementApps);
                                        console.log('Recalculated points for KPI 1.1:', finalBall);
                                      }
                                      checkApp(app.id, finalBall, app, true);
                                    }}>
                                    Tasdiqlash
                                  </Button>
                                  <Button size="sm" colorScheme="red" onClick={() => checkApp(app.id, 0, app, false)}>
                                    Rad etish
                                  </Button>
                                </Box>

                                <Box width="100%">
                                  <Text fontSize="xs" color="gray.600" mb={1}>
                                    Izoh (majburiy, maksimal 80 ta belgi):
                                  </Text>
                                  <Textarea
                                    size="sm"
                                    placeholder="Tasdiqlash yoki rad etish uchun izoh kiriting..."
                                    value={rejectionComments[app.id] || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value.length <= 80) {
                                        setRejectionComments(prev => ({
                                          ...prev,
                                          [app.id]: value
                                        }));
                                      }
                                    }}
                                    maxLength={80}
                                    rows={2}
                                    bg="white"
                                  />
                                  <Text fontSize="xs" color="gray.500" textAlign="right">
                                    {(rejectionComments[app.id] || '').length}/80
                                  </Text>
                                </Box>

                                {/* Show resubmission indicator below comment field */}
                                {app.rejection_comment && app.rejected_at && (() => {
                                  // Collect all department comments
                                  const allComments = [];
                                  if (app.tt_comment) allComments.push({ dept: 'Tarmoq texnologiyalari', comment: app.tt_comment });
                                  if (app.ts_comment) allComments.push({ dept: "Ta'lim sifati", comment: app.ts_comment });
                                  if (app.ov_comment) allComments.push({ dept: "O'quv bo'limi", comment: app.ov_comment });
                                  if (app.mn_comment) allComments.push({ dept: "Ma'naviyat bo'limi", comment: app.mn_comment });

                                  const showDetails = showDetailsForApp[app.id] || false;

                                  return (
                                    <Box
                                      bg="orange.50"
                                      border="1px solid"
                                      borderColor="orange.300"
                                      borderRadius="md"
                                      p={2}
                                      mt={2}
                                      width="100%"
                                    >
                                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Box>
                                          <Text fontSize="xs" fontWeight="600" color="orange.700">
                                            🔄 Qayta yuborilgan
                                          </Text>
                                          <Text fontSize="xs" color="orange.600">
                                            {(() => {
                                              const date = new Date(app.rejected_at);
                                              return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
                                            })()}
                                          </Text>
                                        </Box>
                                        {allComments.length > 0 && (
                                          <Button
                                            size="xs"
                                            colorScheme="orange"
                                            variant="outline"
                                            onClick={() => setShowDetailsForApp(prev => ({
                                              ...prev,
                                              [app.id]: !showDetails
                                            }))}
                                          >
                                            {showDetails ? 'Yashirish' : 'Batafsil'}
                                          </Button>
                                        )}
                                      </Box>

                                      {showDetails && allComments.length > 0 ? (
                                        <Box>
                                          <Text fontSize="xs" fontWeight="600" color="orange.800" mb={1}>
                                            Barcha bo'limlarning izohlari:
                                          </Text>
                                          {allComments.map((item, idx) => (
                                            <Box key={idx} mb={1} pl={2} borderLeft="2px solid" borderColor="orange.400">
                                              <Text fontSize="xs" fontWeight="600" color="gray.700">
                                                {item.dept}:
                                              </Text>
                                              <Text fontSize="xs" color="orange.800">
                                                {item.comment}
                                              </Text>
                                            </Box>
                                          ))}
                                        </Box>
                                      ) : (
                                        <Text fontSize="xs" color="orange.800">
                                          {/* <strong>Sabab:</strong> {app.rejection_comment} */}
                                        </Text>
                                      )}
                                    </Box>
                                  );
                                })()}
                              </Box>
                            );
                          })()}
                        </Box>
                      )}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Card>
      </Box>
    </Layout>
  );
};

export default KpiDetailDepartment;